import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import axios from 'axios';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  Drawer,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardContent,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  InputAdornment,
  Tooltip,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CardActions,
  Stack
} from
  '@mui/material';
import { useIsMobile } from '../hooks/useResponsive';

import TicketCreateView from '../components/Tickets/CreateForm';
import { SaveAsKnownIssue } from '../components/Tickets/SaveAsKnownIssue';
import { SelectExistingSolution } from '../components/Tickets/SelectExistingSolution';
import { SolutionRecommendation } from '../components/Tickets/SolutionRecommendation';
import TicketAttachmentUpload from '../components/Tickets/AttachmentUpload';
import TreeSelect from '../components/TreeSelect'; // Import the TreeSelect component
import { ticketsApi, locationsApi, devicesApi, companiesApi } from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useSiteFilter } from '../contexts/SiteFilterContext';
import { useSnackbar } from 'notistack';
import * as indexedDB from '../utils/indexedDB';
import * as tokenEncryption from '../utils/tokenEncryption';
import { usePersistedFilter, FILTER_KEYS } from '../utils/filterPersistence';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  Person as PersonIcon,
  BusinessCenter as BusinessIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Timeline as TimelineIcon,
  Event as EventIcon,
  MoreVert as MoreVertIcon,
  ChatBubbleOutline as MessageIcon,
  HighlightOff as CancelIcon,
  Flag as FlagIcon,
  BookmarkAdd as BookmarkAddIcon,
  Build as SolutionIcon,
  Construction as BuildIcon,
  Devices as DevicesIcon,
  CloudDownload as DownloadIcon,
  InsertDriveFile as FileIcon,
  NotificationsActive as FollowingIcon,
  NotificationsOff as NotFollowingIcon,
  PictureAsPdf as PdfIcon
} from
  '@mui/icons-material';

/**
 * Get auth token from secure storage with fallback
 */
const getTokenFromStorage = async (key) => {
  try {
    // Try IndexedDB first
    if (indexedDB.isAvailable()) {
      const encryptedToken = await indexedDB.getItem(key);
      if (encryptedToken) {
        const decryptedToken = await tokenEncryption.decryptToken(encryptedToken);
        if (decryptedToken) {
          return decryptedToken;
        }
      }
    }

    // Fallback to localStorage if IndexedDB fails
    const localToken = localStorage.getItem(key);
    if (localToken) {
      return localToken;
    }

    return null;
  } catch (error) {
    console.error('Error retrieving token:', error);
    // Last resort: try localStorage directly
    return localStorage.getItem(key);
  }
};

// Helper function to transform API data to component format
import { useThemeSettings } from "../contexts/ThemeContext"; const transformTicketData = (ticket) => {
  if (!ticket) {
    console.error('transformTicketData called with null/undefined ticket');
    return null;
  }

  // Handle user data - API returns 'name' field (from username)
  const createdByUser = ticket.createdByUser || ticket.created_by_user;
  const transformedCreatedByUser = createdByUser ? {
    id: createdByUser.id,
    email: createdByUser.email,
    name: createdByUser.name || createdByUser.username || 'Unknown',
    // Split name if it contains firstName/lastName, otherwise use name field
    firstName: createdByUser.firstName || (createdByUser.name || createdByUser.username)?.split(' ')[0] || 'Unknown',
    lastName: createdByUser.lastName || (createdByUser.name || createdByUser.username)?.split(' ').slice(1).join(' ') || ''
  } : { firstName: 'Unknown', lastName: '', name: 'Unknown' };

  const supportUser = ticket.supportUser || ticket.support_user;
  const transformedSupportUser = supportUser ? {
    id: supportUser.id,
    email: supportUser.email,
    name: supportUser.name || supportUser.username || 'Unknown',
    firstName: supportUser.firstName || (supportUser.name || supportUser.username)?.split(' ')[0] || 'Unknown',
    lastName: supportUser.lastName || (supportUser.name || supportUser.username)?.split(' ').slice(1).join(' ') || ''
  } : null;

  const transformed = {
    ...ticket,
    id: ticket.id || ticket._id,
    title: ticket.title || ticket.description?.substring(0, 50),
    status: ticket.status || 'Open',
    urgency: ticket.urgency || 'MEDIUM',
    support_type: ticket.support_type || 'other',
    location: ticket.Location || ticket.location || ticket.locationModel,
    locationId: ticket.locationId || ticket.location_id || ticket.Location?.id || ticket.location?.id,
    // Map both deviceId and related_device_id to related_device_id for frontend consistency
    related_device_id: ticket.related_device_id || ticket.relatedDeviceId || ticket.deviceId || ticket.device_id,
    // Use device.name if available, otherwise fallback to related_device_name
    related_device_name: ticket.related_device_name || ticket.relatedDeviceName || (ticket.Device ? ticket.Device.name || ticket.Device.friendlyName : null),
    next_step: ticket.next_step || ticket.nextStep || '',
    support_user_id: ticket.support_user_id || ticket.supportUserId || ticket.supportUser?.id,
    createdByUser: transformedCreatedByUser,
    createdByCompany: ticket.createdByCompany || ticket.created_by_company,
    supportUser: transformedSupportUser,
    supportCompany: ticket.supportCompany || ticket.support_company,
    created_at: ticket.created_at || ticket.createdAt,
    updated_at: ticket.updated_at || ticket.updatedAt,
    // Explicitly preserve nested arrays
    TicketAttachments: ticket.TicketAttachments || [],
    TicketMessages: ticket.TicketMessages || [],
    followers: ticket.followers || [],
    appliedSolutions: ticket.appliedSolutions || []
  };

  //console.log('Transformed ticket - TicketAttachments:', transformed.TicketAttachments?.length || 0);

  return transformed;
};

// Helper function to transform locations data for TreeSelect
const transformLocationsForTreeSelect = (locations) => {
  if (!Array.isArray(locations)) return [];

  // Build location tree recursively (same as CreateForm)
  const buildLocationTree = (locations, parentId = null) => {
    return locations
      .filter((loc) => {
        const locParentId = loc.parentId || loc.parent_id || loc.parentLocation;
        // Handle all falsy values (null, undefined, 0, '', false) as root nodes
        if (parentId === null) {
          return !locParentId || locParentId === 0 || locParentId === '';
        }
        return locParentId === parentId;
      })
      .map((loc) => ({
        id: loc.id || loc._id || loc.location_UUID,
        _id: loc._id || loc.id || loc.location_UUID,
        name: loc.name || loc.friendlyName,
        friendlyName: loc.friendlyName || loc.name,
        locationType: loc.locationType || loc.location_type || 1,
        parentLocation: loc.parentLocation || loc.parent_id || loc.parentId,
        children: buildLocationTree(locations, loc.id || loc._id || loc.location_UUID)
      }));
  };

  // Check if data is already in tree format
  const hasChildren = locations.some((loc) => loc.children && Array.isArray(loc.children));

  if (hasChildren) {
    // Already in tree format, just ensure proper field mapping
    return locations.map((loc) => ({
      id: loc.id || loc._id || loc.location_UUID,
      _id: loc._id || loc.id || loc.location_UUID,
      name: loc.name || loc.friendlyName,
      friendlyName: loc.friendlyName || loc.name,
      locationType: loc.locationType || loc.location_type || 1,
      parentLocation: loc.parentLocation || loc.parent_id || loc.parentId,
      children: loc.children || []
    }));
  } else {
    // Convert flat list to tree
    const tree = buildLocationTree(locations);
    return tree.length > 0 ? tree : locations;
  }
};

// Ticket Management System Component
const TicketManagementSystem = () => {
  const { t } = useThemeSettings();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useIsMobile();
  const { selectedSiteId, siteLocationIds, hasSiteFilter } = useSiteFilter();
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [totalTickets, setTotalTickets] = useState(0);

  // Use persisted filters
  const [viewMode, setViewMode] = usePersistedFilter(FILTER_KEYS.TICKET.VIEW_MODE, 'list');
  const [page, setPage] = usePersistedFilter(FILTER_KEYS.TICKET.PAGE, 0);
  const [rowsPerPage, setRowsPerPage] = usePersistedFilter(FILTER_KEYS.TICKET.ROWS_PER_PAGE, 50);
  const [statusFilter, setStatusFilter] = usePersistedFilter(FILTER_KEYS.TICKET.STATUS_FILTER, ['Open', 'In Progress']);
  const [urgencyFilter, setUrgencyFilter] = usePersistedFilter(FILTER_KEYS.TICKET.URGENCY_FILTER, '');
  const [supportTypeFilter, setSupportTypeFilter] = usePersistedFilter(FILTER_KEYS.TICKET.SUPPORT_TYPE_FILTER, '');
  const [searchFilter, setSearchFilter] = usePersistedFilter(FILTER_KEYS.TICKET.SEARCH, '');
  const [nextStepFilter, setNextStepFilter] = usePersistedFilter(FILTER_KEYS.TICKET.NEXT_STEP_FILTER, '');

  // Local search input state for debouncing (prevents API call on every keystroke)
  const [searchInputValue, setSearchInputValue] = useState(searchFilter);
  const searchDebounceRef = useRef(null);

  // Sorting state
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');

  // Combine into filter object for backward compatibility
  // Ensure status is always an array (could be corrupted in localStorage)
  const filter = {
    status: Array.isArray(statusFilter) ? statusFilter : [],
    urgency: urgencyFilter || '',
    support_type: supportTypeFilter || '',
    search: typeof searchFilter === 'string' ? searchFilter : '',
    next_step: typeof nextStepFilter === 'string' ? nextStepFilter : ''
  };

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saveAsKnownIssueOpen, setSaveAsKnownIssueOpen] = useState(false);
  const [selectSolutionOpen, setSelectSolutionOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    steps_tried: '',
    status: 'Open',
    urgency: 'MEDIUM',
    support_type: '',
    locationId: '',
    general_issue: false
  });
  const [devices, setDevices] = useState([]);

  // TreeSelect states - replacing the old location dialog
  const [locationTreeSelectOpen, setLocationTreeSelectOpen] = useState(false);
  const [editLocationTreeSelectOpen, setEditLocationTreeSelectOpen] = useState(false);
  const [locationsForTreeSelect, setLocationsForTreeSelect] = useState([]);

  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [supportUsers, setSupportUsers] = useState([]);
  const [loadingSupportUsers, setLoadingSupportUsers] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [loadingFollow, setLoadingFollow] = useState(false);

  // Physical intervention states
  const [interventionDialogOpen, setInterventionDialogOpen] = useState(false);
  const [interventionDate, setInterventionDate] = useState('');
  const [interventionTime, setInterventionTime] = useState('09:00');
  const [interventionNotes, setInterventionNotes] = useState('');
  const [interventionDeviceId, setInterventionDeviceId] = useState('');
  const [interventionDevices, setInterventionDevices] = useState([]);
  const [loadingInterventionDevices, setLoadingInterventionDevices] = useState(false);
  const [sendingIntervention, setSendingIntervention] = useState(false);
  const [linkedIntervention, setLinkedIntervention] = useState(null);
  const [linkedInterventions, setLinkedInterventions] = useState([]);

  // Reschedule intervention states
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('09:00');
  const [rescheduleNotes, setRescheduleNotes] = useState('');
  const [reschedulingIntervention, setReschedulingIntervention] = useState(false);

  // Cancel intervention states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingIntervention, setCancellingIntervention] = useState(false);

  // Intervention selection dialog states
  const [interventionSelectionDialogOpen, setInterventionSelectionDialogOpen] = useState(false);

  // Support types state
  const [supportTypes, setSupportTypes] = useState([]);
  const [loadingSupportTypes, setLoadingSupportTypes] = useState(false);

  // Get user data and 
  // ons
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const userRole = user?.role || user?.userRole || 'user';
  const isSupport = userRole === 'support' || userRole === 'admin' || hasPermission('tickets.manage');

  // Debounce search input - only trigger API call after 2 seconds of no typing
  useEffect(() => {
    // Clear any existing timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Only set up debounce if the value has actually changed from the current filter
    if (searchInputValue !== searchFilter) {
      searchDebounceRef.current = setTimeout(() => {
        setSearchFilter(searchInputValue);
        setPage(0); // Reset to first page when search changes
      }, 2000);
    }

    // Cleanup on unmount or when value changes
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInputValue]);

  // Sync searchInputValue when searchFilter changes externally (e.g., reset)
  useEffect(() => {
    setSearchInputValue(searchFilter);
  }, [searchFilter]);

  // Fetch data effects - also re-fetch when site filter or client filters change
  useEffect(() => {
    fetchTickets();
  }, [page, rowsPerPage, selectedSiteId, siteLocationIds, statusFilter, urgencyFilter, supportTypeFilter, searchFilter, nextStepFilter]);

  useEffect(() => {
    fetchLocations();
    fetchSupportTypes();
    if (isSupport) {
      fetchSupportUsers();
    }
  }, []);

  // Handle URL parameter for ticket selection
  useEffect(() => {
    const selectedId = searchParams.get('selectedId');
    if (selectedId && tickets.length > 0) {
      const ticket = tickets.find((t) => String(t.id) === String(selectedId));
      // Only select if it's a different ticket than currently selected
      if (ticket && (!selectedTicket || String(selectedTicket.id) !== String(selectedId))) {
        // Call handleTicketSelect to properly initialize everything including interventions
        handleTicketSelect(ticket);
      }
    }
  }, [searchParams, tickets]);

  const fetchTickets = async () => {
    try {
      setLoading(true);

      // Calculate page number (API is 1-indexed, UI is 0-indexed)
      const apiPage = page + 1;
      const limit = rowsPerPage === -1 ? 999999 : rowsPerPage; // -1 means "All"

      // Build query parameters including filters for server-side filtering
      const queryParams = { page: apiPage, limit };

      // Add status filter - send as comma-separated string if array
      if (statusFilter && statusFilter.length > 0) {
        queryParams.status = Array.isArray(statusFilter) ? statusFilter.join(',') : statusFilter;
      }

      // Add urgency filter
      if (urgencyFilter) {
        queryParams.urgency = urgencyFilter;
      }

      // Add support type filter
      if (supportTypeFilter) {
        queryParams.support_type = supportTypeFilter;
      }

      // Add search filter
      if (searchFilter) {
        queryParams.search = searchFilter;
      }

      const response = await ticketsApi.getAll(queryParams);
      //console.log('Tickets API response:', response);

      let ticketsData = [];
      let pagination = null;

      if (response?.data) {
        if (Array.isArray(response.data)) {
          ticketsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          ticketsData = response.data.data;
          pagination = response.data.pagination;
        } else if (response.data.tickets && Array.isArray(response.data.tickets)) {
          ticketsData = response.data.tickets;
          pagination = response.data.pagination;
        }
      }

      let transformedTickets = ticketsData.map(transformTicketData);

      // Filter by site - when hasSiteFilter is true and siteLocationIds is set (even if empty array for no-access users)
      if (hasSiteFilter && siteLocationIds) {
        transformedTickets = transformedTickets.filter(ticket => {
          const ticketLocationId = ticket.locationId || ticket.location?.id;
          return siteLocationIds.includes(Number(ticketLocationId)) || siteLocationIds.includes(String(ticketLocationId));
        });
        console.log(`[Tickets] Filtered to ${transformedTickets.length} tickets for selected site`);
      }

      setTickets(transformedTickets);

      // Set total count - if we filtered by site, use the filtered count
      if (hasSiteFilter && siteLocationIds) {
        setTotalTickets(transformedTickets.length);
      } else if (pagination && pagination.total !== undefined) {
        setTotalTickets(pagination.total);
      } else {
        setTotalTickets(transformedTickets.length);
      }

      setError(null);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError('Failed to load tickets. Please try again later.');
      setTickets([]);
      setTotalTickets(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationsApi.getAll();
      //console.log('Locations API response:', response);

      let locationsData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          locationsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          locationsData = response.data.data;
        } else if (response.data.locations && Array.isArray(response.data.locations)) {
          locationsData = response.data.locations;
        }
      }

      // Transform locations to expected format - keeping all fields needed for site lookup
      const transformedLocations = locationsData.map((loc) => ({
        id: loc.id || loc._id,
        _id: loc._id || loc.id,
        friendlyName: loc.friendlyName || loc.name || `Location ${loc.id || loc._id}`,
        name: loc.name || loc.friendlyName,
        parentId: loc.parentId || loc.parent_id || loc.parentLocation || loc.parent,
        locationType: loc.locationType || loc.location_type || loc.locationTypeModel?.id || loc.locationTypeModel?.value,
        locationTypeModel: loc.locationTypeModel
      }));

      // Transform locations for TreeSelect component
      const treeSelectLocations = transformLocationsForTreeSelect(locationsData);

      setLocations(transformedLocations);
      setLocationsForTreeSelect(treeSelectLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      // Use empty array if fetch fails
      setLocations([]);
      setLocationsForTreeSelect([]);
    }
  };

  const fetchSupportTypes = async () => {
    try {
      setLoadingSupportTypes(true);
      const response = await companiesApi.getSupportTypes();
      //console.log('Support types API response:', response);

      let supportTypesData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          supportTypesData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          supportTypesData = response.data.data;
        } else if (response.data.supportTypes && Array.isArray(response.data.supportTypes)) {
          supportTypesData = response.data.supportTypes;
        }
      }

      setSupportTypes(supportTypesData);
    } catch (error) {
      console.error('Error fetching support types:', error);
      // Use empty array if fetch fails
      setSupportTypes([]);
    } finally {
      setLoadingSupportTypes(false);
    }
  };

  const fetchDevices = async (locationId = null, forIntervention = false) => {
    try {
      if (forIntervention) {
        setLoadingInterventionDevices(true);
      } else {
        setLoadingDevices(true);
      }

      //console.log('Fetching devices for location:', locationId);

      let response;

      // Try to use the location-specific endpoint if locationId is provided
      if (locationId && devicesApi.getByLocation) {
        try {
          response = await devicesApi.getByLocation(locationId);
        } catch (locationError) {
          console.warn('Location-specific device fetch failed, falling back to all devices:', locationError.message);
          // Fall back to getting all devices and filtering
          response = await devicesApi.getAll();
        }
      } else {
        // Get all devices
        response = await devicesApi.getAll();
      }

      //console.log('Devices API response:', response);

      let devicesData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          devicesData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          devicesData = response.data.data;
        } else if (response.data.devices && Array.isArray(response.data.devices)) {
          devicesData = response.data.devices;
        }
      }

      // Transform devices to expected format
      let transformedDevices = devicesData.map((device) => ({
        id: device.id || device._id,
        name: device.friendlyName || device.name || `Device ${device.id || device._id}`,
        friendlyName: device.friendlyName || device.name,
        deviceType: device.deviceType || device.device_type,
        // Fix the location mapping - the API uses deviceLocation field
        locationId: device.deviceLocation || device.locationId || device.location_id || device.location?.id
      }));

      // If we got all devices but wanted specific location, filter them
      if (locationId && response === (await devicesApi.getAll())) {
        transformedDevices = transformedDevices.filter((device) =>
          device.locationId === locationId || device.locationId === parseInt(locationId)
        );
      }

      //console.log('Transformed devices:', transformedDevices);
      //console.log('Devices count:', transformedDevices.length);
      //console.log('Location filter:', locationId);

      if (forIntervention) {
        setInterventionDevices(transformedDevices);
      } else {
        setDevices(transformedDevices);
      }

      // Show feedback if no devices found
      if (transformedDevices.length === 0 && locationId) {
        const locationName = locations.find((l) => l.id === locationId || l.id === parseInt(locationId))?.friendlyName || 'this location';
        const message = `No devices found at ${locationName}`;
        if (forIntervention) {
          //console.log(message + ' for intervention');
        } else {
          enqueueSnackbar(message, { variant: 'info' });
        }
      }

    } catch (error) {
      console.error('Error fetching devices:', error);

      // Enhanced error handling with more specific error messages
      let errorMessage = 'Failed to load devices';
      if (error.response?.status === 404) {
        errorMessage = 'Location not found or has no devices';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied to view devices at this location';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      if (forIntervention) {
        setInterventionDevices([]);
        enqueueSnackbar(`Failed to load devices for intervention: ${errorMessage}`, { variant: 'error' });
      } else {
        setDevices([]);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } finally {
      if (forIntervention) {
        setLoadingInterventionDevices(false);
      } else {
        setLoadingDevices(false);
      }
    }
  };

  const fetchSupportUsers = async () => {
    try {
      setLoadingSupportUsers(true);
      const token = await getTokenFromStorage('auth_token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      //console.log('Support users API response:', response);

      let usersData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          usersData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          usersData = response.data.data;
        } else if (response.data.users && Array.isArray(response.data.users)) {
          usersData = response.data.users;
        }
      }

      // Filter for support/admin users only
      const supportUsersData = usersData.filter((u) =>
        u.role === 'support' || u.role === 'admin' || u.userRole === 'support' || u.userRole === 'admin'
      );

      setSupportUsers(supportUsersData);
    } catch (error) {
      console.error('Error fetching support users:', error);
      setSupportUsers([]);
    } finally {
      setLoadingSupportUsers(false);
    }
  };

  const checkFollowStatus = async (ticketId) => {
    try {
      const response = await ticketsApi.getFollowers(ticketId);

      if (response?.data?.success && response.data.followers) {
        const followers = response.data.followers;
        setFollowersCount(followers.length);

        // Check if current user is in the followers list
        const currentUserId = user?.id || user?._id;
        //console.log('Current user ID:', currentUserId);
        //console.log('Followers:', followers);

        // Check against different possible ID fields in the response
        const userIsFollowing = followers.some((follower) =>
          follower.id === currentUserId ||
          follower.userId === currentUserId ||
          follower.user_id === currentUserId
        );

        //console.log('User is following:', userIsFollowing);
        setIsFollowing(userIsFollowing);
      } else if (response?.data?.data) {
        // Alternative API response format with data array
        const followers = response.data.data;
        setFollowersCount(followers.length);

        // Check if current user is in the followers list
        const currentUserId = user?.id || user?._id;
        //console.log('Current user ID:', currentUserId);
        //console.log('Followers (alt format):', followers);

        // Check against different possible ID fields in the response
        const userIsFollowing = followers.some((follower) =>
          follower.id === currentUserId ||
          follower.userId === currentUserId ||
          follower.user_id === currentUserId ||
          follower.user && follower.user.id === currentUserId
        );

        //console.log('User is following (alt format):', userIsFollowing);
        setIsFollowing(userIsFollowing);
      } else {
        console.warn('Unexpected follower response format:', response?.data);
        setIsFollowing(false);
        setFollowersCount(0);
      }
    } catch (error) {
      console.error('Error checking follow status:', error);
      // If endpoint doesn't exist or returns error, assume not following
      setIsFollowing(false);
      setFollowersCount(0);
    }
  };

  const toggleFollow = async () => {
    if (!selectedTicket || !selectedTicket.id || loadingFollow) return;

    try {
      setLoadingFollow(true);

      if (isFollowing) {
        const response = await ticketsApi.unfollowTicket(selectedTicket.id);
        if (response?.data?.success || response?.status === 200) {
          setIsFollowing(false);
          setFollowersCount((prev) => Math.max(0, prev - 1));
          enqueueSnackbar('You have unfollowed this ticket', { variant: 'info' });
        }
      } else {
        try {
          const response = await ticketsApi.followTicket(selectedTicket.id);
          if (response?.data?.success || response?.status === 200) {
            setIsFollowing(true);
            setFollowersCount((prev) => prev + 1);
            enqueueSnackbar('You are now following this ticket', { variant: 'success' });
          }
        } catch (followError) {
          // Check if already following
          if (followError?.response?.data?.error?.includes('already following')) {
            setIsFollowing(true);
            enqueueSnackbar('You are already following this ticket', { variant: 'info' });
          } else {
            throw followError;
          }
        }
      }
    } catch (error) {
      console.error('Error toggling follow status:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to update follow status';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoadingFollow(false);
    }
  };

  const handleInterventionButtonClick = async () => {
    //console.log('Intervention button clicked!');
    //console.log('Current interventionDialogOpen state:', interventionDialogOpen);

    // Open the dialog first
    setInterventionDialogOpen(true);
    //console.log('Set interventionDialogOpen to true');

    // Fetch devices for the ticket's location
    const locationId = selectedTicket.locationId || selectedTicket.location?.id;
    if (locationId) {
      //console.log('Fetching devices for intervention, locationId:', locationId);
      await fetchDevices(locationId, true); // Use the updated fetchDevices with forIntervention flag
    } else {
      //console.log('No location ID found for ticket, skipping device fetch');
      setInterventionDevices([]);
    }
  };

  const handleRequestIntervention = async () => {
    //console.log('handleRequestIntervention called');
    //console.log('selectedTicket:', selectedTicket);
    //console.log('interventionDate:', interventionDate);
    //console.log('interventionNotes:', interventionNotes);
    //console.log('interventionDeviceId:', interventionDeviceId);

    if (!selectedTicket || !selectedTicket.id) {
      console.error('No ticket selected or missing ticket ID');
      enqueueSnackbar('No ticket selected', { variant: 'error' });
      return;
    }

    try {
      setSendingIntervention(true);
      //console.log('Sending intervention request...');

      const requestData = {
        interventionDate: interventionDate || null,
        interventionTime: interventionTime || '09:00',
        notes: interventionNotes || '',
        deviceId: interventionDeviceId || null
      };
      //console.log('Request data:', requestData);

      const response = await ticketsApi.requestPhysicalIntervention(selectedTicket.id, requestData);

      if (response?.data?.success || response?.status === 200) {
        let successMessage = 'Physical intervention request sent successfully';

        // Add intervention date to success message if provided
        if (interventionDate) {
          const formattedDate = new Date(interventionDate).toLocaleDateString('en-GB', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
          successMessage += ` for ${formattedDate}`;
        }

        // Store intervention ID if returned - check multiple possible locations
        const interventionId = response?.data?.intervention_id || response?.data?.interventionId || response?.data?.data?.intervention_id;

        enqueueSnackbar(successMessage, { variant: 'success' });

        // Show option to view intervention report if created
        if (interventionId) {
          enqueueSnackbar(
            'Field intervention report created',
            {
              variant: 'info',
              action: (key) => (
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => {
                    navigate(`/field-interventions/${interventionId}`);
                  }}
                >
                  View Report
                </Button>
              )
            }
          );
        }

        // Refresh ticket messages to show the system message
        await fetchTicketMessages(selectedTicket.id);

        // Wait a moment for the backend to fully create the intervention
        await new Promise(resolve => setTimeout(resolve, 500));

        // Reload the ticket to refresh linked interventions
        await handleTicketSelect(selectedTicket);

        // Force a second fetch if no interventions loaded
        setTimeout(async () => {
          const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
          const token = await getTokenFromStorage('auth_token');
          const checkResponse = await fetch(`${API_URL}/api/field-interventions?ticket_id=${selectedTicket.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (checkResponse.ok) {
            const data = await checkResponse.json();
            if (data.interventions && data.interventions.length > 0) {
              setLinkedInterventions(data.interventions);
              const sortedInterventions = [...data.interventions].sort((a, b) =>
                new Date(b.created_at) - new Date(a.created_at)
              );
              setLinkedIntervention(sortedInterventions[0]);
            }
          }
        }, 1500);

        // Close dialog and reset form
        setInterventionDialogOpen(false);
        setInterventionDate('');
        setInterventionTime('09:00');
        setInterventionNotes('');
        setInterventionDeviceId('');
        setInterventionDevices([]);

        //console.log('Intervention request completed successfully');
      } else {
        console.warn('Unexpected response format:', response);
        enqueueSnackbar('Intervention request sent but response format was unexpected', { variant: 'warning' });
      }
    } catch (error) {
      console.error('Error requesting physical intervention:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to request physical intervention';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSendingIntervention(false);
      //console.log('handleRequestIntervention finished');
    }
  };

  const handleRescheduleButtonClick = () => {
    if (!linkedIntervention) return;

    // Pre-fill with current intervention date/time if available
    if (linkedIntervention.scheduled_date) {
      const date = new Date(linkedIntervention.scheduled_date);
      setRescheduleDate(date.toISOString().split('T')[0]);

      // Extract time if available
      if (linkedIntervention.scheduled_time) {
        setRescheduleTime(linkedIntervention.scheduled_time);
      }
    }

    setRescheduleDialogOpen(true);
  };

  const handleRescheduleIntervention = async () => {
    if (!linkedIntervention || !selectedTicket) return;

    if (!rescheduleDate) {
      enqueueSnackbar('Please select a new date for the intervention', { variant: 'warning' });
      return;
    }

    try {
      setReschedulingIntervention(true);

      const requestData = {
        newDate: rescheduleDate,
        newTime: rescheduleTime || '09:00',
        notes: rescheduleNotes || ''
      };

      const response = await ticketsApi.rescheduleIntervention(
        selectedTicket.id,
        linkedIntervention.id,
        requestData
      );

      if (response?.data?.success) {
        enqueueSnackbar('Intervention rescheduled successfully. Calendar items have been updated.', { variant: 'success' });

        // Close dialog and reset form
        setRescheduleDialogOpen(false);
        setRescheduleDate('');
        setRescheduleTime('09:00');
        setRescheduleNotes('');

        // Refresh interventions
        await handleTicketSelect(selectedTicket);

        // Refresh ticket details
        await fetchTickets();
      } else {
        enqueueSnackbar('Intervention rescheduled but response format was unexpected', { variant: 'warning' });
      }
    } catch (error) {
      console.error('Error rescheduling intervention:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to reschedule intervention';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setReschedulingIntervention(false);
    }
  };

  const handleCancelButtonClick = () => {
    if (!linkedIntervention) return;
    setCancelDialogOpen(true);
  };

  const handleCancelIntervention = async () => {
    if (!linkedIntervention || !selectedTicket) return;

    if (!cancelReason || cancelReason.trim() === '') {
      enqueueSnackbar('Please provide a reason for cancelling the intervention', { variant: 'warning' });
      return;
    }

    try {
      setCancellingIntervention(true);

      const requestData = {
        reason: cancelReason
      };

      const response = await ticketsApi.cancelIntervention(
        selectedTicket.id,
        linkedIntervention.id,
        requestData
      );

      if (response?.data?.success) {
        enqueueSnackbar('Intervention cancelled successfully. Calendar items have been removed.', { variant: 'success' });

        // Close dialog and reset form
        setCancelDialogOpen(false);
        setCancelReason('');

        // Refresh interventions
        await handleTicketSelect(selectedTicket);

        // Refresh ticket details
        await fetchTickets();
      } else {
        enqueueSnackbar('Intervention cancelled but response format was unexpected', { variant: 'warning' });
      }
    } catch (error) {
      console.error('Error cancelling intervention:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to cancel intervention';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setCancellingIntervention(false);
    }
  };

  const handleViewReportClick = () => {
    // Check if there are multiple interventions
    if (linkedInterventions && linkedInterventions.length > 1) {
      setInterventionSelectionDialogOpen(true);
    } else if (linkedIntervention) {
      navigate(`/field-interventions/${linkedIntervention.id}`);
    }
  };

  useEffect(() => {
    if (selectedTicket && selectedTicket.id && (viewMode === 'view' || viewMode === 'edit')) {
      fetchTicketMessages(selectedTicket.id);
      checkFollowStatus(selectedTicket.id);
      // Fetch devices for the ticket's location
      const locationId = selectedTicket.locationId || selectedTicket.location?.id;
      if (locationId) {
        fetchDevices(locationId);
      } else {
        // Fetch all devices if no location specified
        fetchDevices();
      }
    }
  }, [selectedTicket, viewMode]);

  const fetchTicketMessages = async (ticketId) => {
    try {
      // Fetch full ticket details including messages
      const response = await ticketsApi.getById(ticketId);
      const ticketData = response.data.data.ticket;

      // Extract messages from ticket data and reverse to show newest first
      if (ticketData.TicketMessages && Array.isArray(ticketData.TicketMessages)) {
        setTicketMessages([...ticketData.TicketMessages].reverse());
      } else {
        setTicketMessages([]);
      }
    } catch (error) {
      console.error('Error fetching ticket messages:', error);
      setTicketMessages([]);
    }
  };

  // Location selection handlers for TreeSelect
  const handleLocationSelect = (location) => {
    if (viewMode === 'edit' && selectedTicket) {
      // In edit mode - update the selected ticket
      handleSelectedTicketChange('locationId', location?.id || null);
      setEditLocationTreeSelectOpen(false);
    } else {
      // In other contexts (like create mode)
      handleNewTicketChange('locationId', location?.id || null);
      setLocationTreeSelectOpen(false);
    }
  };

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (name, value) => {
    // Reset to first page when filters change to avoid pagination issues
    setPage(0);
    // Update individual filter states
    switch(name) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'urgency':
        setUrgencyFilter(value);
        break;
      case 'support_type':
        setSupportTypeFilter(value);
        break;
      case 'search':
        setSearchFilter(value);
        break;
      case 'next_step':
        setNextStepFilter(value);
        break;
      default:
        break;
    }
  };

  const handleResetFilter = () => {
    setPage(0); // Reset to first page when filters reset
    setStatusFilter(['Open', 'In Progress']);
    setUrgencyFilter('');
    setSupportTypeFilter('');
    setSearchFilter('');
    setNextStepFilter('');
  };

  const handleTicketSelect = async (ticket) => {
    setViewMode('view');

    // Update URL parameter
    if (ticket?.id) {
      setSearchParams({ selectedId: ticket.id });

      // Fetch full ticket details from API to get all attachments and messages
      try {
        //console.log('Fetching full ticket details for ticket:', ticket.id);
        const ticketResponse = await ticketsApi.getById(ticket.id);
        const fullTicketData = ticketResponse.data.data || ticketResponse.data;
        //console.log('Full ticket data received:', fullTicketData);

        // Transform and set the full ticket data
        const transformedTicket = transformTicketData(fullTicketData.ticket || fullTicketData);
        //console.log('Transformed ticket data:', transformedTicket);
        //console.log('Attachments count:', transformedTicket.TicketAttachments?.length);
        setSelectedTicket(transformedTicket);
      } catch (error) {
        console.error('Error fetching full ticket details:', error);
        // Fallback to using the ticket from the list
        setSelectedTicket(ticket);
      }

      // Fetch linked intervention if exists
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
        const token = await getTokenFromStorage('auth_token');
        const response = await fetch(`${API_URL}/api/field-interventions?ticket_id=${ticket.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.interventions && data.interventions.length > 0) {
            setLinkedInterventions(data.interventions);
            // Set the latest intervention (most recent one)
            const sortedInterventions = [...data.interventions].sort((a, b) =>
              new Date(b.created_at) - new Date(a.created_at)
            );
            setLinkedIntervention(sortedInterventions[0]);
          } else {
            setLinkedInterventions([]);
            setLinkedIntervention(null);
          }
        }
      } catch (error) {
        console.error('Error fetching linked intervention:', error);
        setLinkedInterventions([]);
        setLinkedIntervention(null);
      }
    }
  };

  const handleCreateTicket = () => {
    setNewTicket({
      title: '',
      description: '',
      steps_tried: '',
      status: 'Open',
      urgency: 'MEDIUM',
      support_type: '',
      locationId: '',
      general_issue: false
    });
    setViewMode('create');
  };

  const handleEditTicket = () => {
    setViewMode('edit');
  };

  const handleDeleteTicket = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTicket = async () => {
    try {
      await ticketsApi.delete(selectedTicket.id);
      setTickets(tickets.filter((ticket) => ticket.id !== selectedTicket.id));
      setDeleteDialogOpen(false);
      setViewMode('list');
      setSelectedTicket(null);
      setSearchParams({}); // Clear URL parameter
      enqueueSnackbar(`Ticket #${selectedTicket.id} deleted successfully`, { variant: 'success' });
    } catch (error) {
      console.error('Error deleting ticket:', error);
      enqueueSnackbar('Failed to delete ticket. Please try again.', { variant: 'error' });
    }
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedTicket(null);

    // Clear URL parameter
    setSearchParams({});
  };

  const handleSaveTicket = async () => {
    if (viewMode === 'create') {
      try {
        const ticketData = {
          ...newTicket,
          location_id: parseInt(newTicket.locationId)
        };
        const response = await ticketsApi.create(ticketData);

        // Handle different response structures
        let ticketDataFromResponse = response.data;
        if (response.data.data) {
          ticketDataFromResponse = response.data.data;
        }
        if (ticketDataFromResponse.ticket) {
          ticketDataFromResponse = ticketDataFromResponse.ticket;
        }

        const createdTicket = transformTicketData(ticketDataFromResponse);
        setTickets([...tickets, createdTicket]);
        enqueueSnackbar(`Ticket #${createdTicket.id} created successfully!`, { variant: 'success' });

        // Navigate to the newly created ticket
        await handleTicketSelect(createdTicket);
      } catch (error) {
        console.error('Error creating ticket:', error);
        enqueueSnackbar('Failed to create ticket. Please try again.', { variant: 'error' });
      }
    } else if (viewMode === 'edit' && selectedTicket) {
      try {
        const updateData = {
          title: selectedTicket.title,
          description: selectedTicket.description,
          steps_tried: selectedTicket.steps_tried,
          status: selectedTicket.status,
          urgency: selectedTicket.urgency,
          support_type: selectedTicket.support_type,
          location_id: selectedTicket.locationId,
          next_step: selectedTicket.next_step,
          // Fix for related device issue - use deviceId instead of related_device_id
          deviceId: selectedTicket.related_device_id || selectedTicket.deviceId || null,
          device_issue_type: selectedTicket.device_issue_type || null,
          // Keep these fields for backward compatibility
          related_device_id: selectedTicket.related_device_id || selectedTicket.deviceId || null,
          related_device_name: selectedTicket.related_device_name || null,
          support_user_id: selectedTicket.support_user_id || selectedTicket.supportUser?.id || null
        };
        const response = await ticketsApi.update(selectedTicket.id, updateData);
        const updatedTicket = transformTicketData(response.data);
        const updatedTickets = tickets.map((ticket) =>
          ticket.id === selectedTicket.id ? updatedTicket : ticket
        );
        setTickets(updatedTickets);
        setSelectedTicket(updatedTicket);
        setViewMode('view');

        // Send email notification for ticket update
        try {
          const emailData = {
            ...updatedTicket,
            creatorEmail: updatedTicket.createdByUser?.email,
            creatorName: updatedTicket.createdByUser?.name || (
              updatedTicket.createdByUser ? `${updatedTicket.createdByUser.firstName} ${updatedTicket.createdByUser.lastName}` : 'User')
          };
          const updatedBy = user?.name || (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Support Team');

          // Email notifications are now handled by api.caro.care
        } catch (emailError) {
          console.error('Failed to send update notification:', emailError);
          // Don't fail the update if email fails
        }
      } catch (error) {
        console.error('Error updating ticket:', error);
        enqueueSnackbar('Failed to update ticket. Please try again.', { variant: 'error' });
      }
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && selectedFiles.length === 0) return;

    try {
      setUploadingFiles(true);

      // Send message to API
      const response = await ticketsApi.addComment(selectedTicket.id, { message: newMessage || 'File attachment' });
      const newMsg = response.data;

      // Upload files if any
      if (selectedFiles.length > 0) {
        const formData = new FormData();
        selectedFiles.forEach((file) => {
          formData.append('attachments', file);
        });

        try {
          await ticketsApi.addAttachment(selectedTicket.id, formData);
        } catch (uploadError) {
          console.error('Error uploading files:', uploadError);
          enqueueSnackbar('Message sent but file upload failed', { variant: 'warning' });
        }
      }

      // Enrich message with current user data if not present
      const enrichedMsg = {
        ...newMsg,
        User: newMsg.user || newMsg.User || {
          id: user?.id,
          email: user?.email,
          username: user?.name || user?.username || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
          firstName: user?.firstName,
          lastName: user?.lastName
        },
        created_at: newMsg.created_at || new Date().toISOString(),
        attachments: selectedFiles.length > 0 ? selectedFiles.map((f) => ({ originalName: f.name, uploading: true })) : []
      };

      // Add the new message to the local state (at the beginning since we show newest first)
      setTicketMessages([enrichedMsg, ...ticketMessages]);
      setNewMessage('');
      setSelectedFiles([]);

      // Refresh messages to get attachment URLs
      if (selectedFiles.length > 0) {
        setTimeout(() => fetchTicketMessages(selectedTicket.id), 1000);
      }

      enqueueSnackbar('Message sent successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error sending message:', error);
      enqueueSnackbar('Failed to send message. Please try again.', { variant: 'error' });
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleNewTicketChange = (field, value) => {
    setNewTicket((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectedTicketChange = (field, value) => {
    setSelectedTicket((prev) => ({ ...prev, [field]: value }));
  };

  // Wildcard search helper function
  // Converts wildcard pattern (with *) to regex and tests against text
  const wildcardMatch = (text, pattern) => {
    if (!text || !pattern) return false;
    // Ensure both text and pattern are strings
    const textStr = String(text);
    const patternStr = String(pattern);

    // If no wildcard, use simple includes for better performance
    if (!patternStr.includes('*')) {
      return textStr.toLowerCase().includes(patternStr.toLowerCase());
    }

    // Convert wildcard pattern to regex
    // Escape special regex characters except *
    const regexPattern = patternStr
      .toLowerCase()
      .split('*')
      .map(part => part.replace(/[.+?^${}()|[\]\\]/g, '\\$&'))
      .join('.*');

    // ^ and $ for exact pattern matching with wildcards
    const regex = new RegExp(`^${regexPattern}$`, 'i');
    return regex.test(textStr.toLowerCase());
  };

  // Check if search term matches any field in ticket
  const matchesSearch = (ticket, searchTerm) => {
    if (!searchTerm) return true;

    const fields = [
      ticket.title,
      ticket.description,
      ticket.location?.friendlyName,
      ticket.related_device_name,
      ticket.createdByUser?.name,
      ticket.createdByUser?.email,
      ticket.next_step
    ];

    return fields.some(field => field && wildcardMatch(field, searchTerm));
  };

  // Filter tickets based on filter state
  const filteredTickets = tickets.filter((ticket) => {
    // Check next_step filter - uses wildcard matching for as-you-type filtering
    const matchesNextStep = !filter.next_step ||
      (ticket.next_step && wildcardMatch(ticket.next_step, filter.next_step));

    return (
      (filter.status.length === 0 || filter.status.includes(ticket.status)) &&
      (filter.urgency === '' || ticket.urgency === filter.urgency) &&
      (filter.support_type === '' || ticket.support_type === filter.support_type) &&
      matchesNextStep &&
      matchesSearch(ticket, filter.search)
    );
  });

  // Sort handler
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Comparator for sorting
  const getComparator = (orderDir, orderByField) => {
    return orderDir === 'desc'
      ? (a, b) => descendingComparator(a, b, orderByField)
      : (a, b) => -descendingComparator(a, b, orderByField);
  };

  const descendingComparator = (a, b, orderByField) => {
    let aValue, bValue;

    switch (orderByField) {
      case 'id':
        aValue = a.id;
        bValue = b.id;
        break;
      case 'title':
        aValue = (a.title || a.description || '').toLowerCase();
        bValue = (b.title || b.description || '').toLowerCase();
        break;
      case 'status':
        aValue = a.status || '';
        bValue = b.status || '';
        break;
      case 'urgency':
        // Custom order: HIGH > MEDIUM > LOW
        const urgencyOrder = { 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
        aValue = urgencyOrder[a.urgency] || 0;
        bValue = urgencyOrder[b.urgency] || 0;
        break;
      case 'support_type':
        aValue = a.support_type || '';
        bValue = b.support_type || '';
        break;
      case 'next_step':
        aValue = (a.next_step || '').toLowerCase();
        bValue = (b.next_step || '').toLowerCase();
        break;
      case 'location':
        aValue = (a.location?.friendlyName || '').toLowerCase();
        bValue = (b.location?.friendlyName || '').toLowerCase();
        break;
      case 'device':
        aValue = (a.related_device_name || '').toLowerCase();
        bValue = (b.related_device_name || '').toLowerCase();
        break;
      case 'createdAt':
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
        break;
      case 'updatedAt':
        aValue = new Date(a.updatedAt || 0).getTime();
        bValue = new Date(b.updatedAt || 0).getTime();
        break;
      default:
        aValue = a[orderByField] || '';
        bValue = b[orderByField] || '';
    }

    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  };

  // Sort and paginate tickets
  const sortedTickets = [...filteredTickets].sort(getComparator(order, orderBy));

  // Note: Pagination is now handled by the API, so we use tickets directly
  // Client-side filtering still applies (for search, status, etc.)
  const paginatedTickets = sortedTickets;

  // Helper function to check if a location is a site (locationType = 1)
  const isLocationTypeSite = (loc) => {
    if (!loc) return false;
    // Check all possible fields where locationType could be stored
    const locType = loc.locationType
      || loc.location_type
      || loc.locationTypeModel?.id
      || loc.locationTypeModel?.value
      || loc.LocationType?.id
      || loc.locationTypeId;
    return locType === 1;
  };

  // Helper function to find the site (locationType = 1) for a location
  const findSiteForLocation = (locationId) => {
    if (!locationId || !locations || locations.length === 0) return null;

    // Build a map for quick lookups with multiple key types
    const locationMap = new Map();
    locations.forEach(loc => {
      const id = loc.id || loc._id;
      if (id) {
        locationMap.set(id, loc);
        locationMap.set(String(id), loc);
        locationMap.set(Number(id), loc);
      }
      if (loc._id) {
        locationMap.set(loc._id, loc);
        locationMap.set(String(loc._id), loc);
      }
    });

    // Find the starting location
    let currentLocation = locationMap.get(locationId)
      || locationMap.get(String(locationId))
      || locationMap.get(Number(locationId));

    if (!currentLocation) return null;

    // Check if current location is already a site
    if (isLocationTypeSite(currentLocation)) {
      return currentLocation;
    }

    // Traverse up the parent chain to find locationType = 1 (site)
    let iterations = 0;
    const maxIterations = 100;
    let highestParent = currentLocation;

    while (iterations < maxIterations) {
      const parentId = currentLocation.parentId
        || currentLocation.parent_id
        || currentLocation.parentLocation
        || currentLocation.parent;

      if (!parentId) break;

      const parentLocation = locationMap.get(parentId)
        || locationMap.get(String(parentId))
        || locationMap.get(Number(parentId));

      if (!parentLocation) break;

      highestParent = parentLocation;

      // Check if parent is a site (locationType = 1)
      if (isLocationTypeSite(parentLocation)) {
        return parentLocation;
      }

      currentLocation = parentLocation;
      iterations++;
    }

    // If no site found with locationType = 1, return the highest parent (root)
    return highestParent;
  };

  // PDF Export function - Card layout with CARO branding
  const handleExportPdf = async () => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const headerHeight = 25;
    const footerHeight = 15;
    const contentWidth = pageWidth - (2 * margin);
    const contentAreaTop = margin + headerHeight;
    const contentAreaBottom = pageHeight - footerHeight;
    let yPosition = contentAreaTop;

    // Brand colors (matching CARO style)
    const plumMain = [154, 63, 105];
    const plumDark = [112, 44, 77];
    const grey800 = [28, 37, 46];
    const grey500 = [145, 158, 171];
    const grey300 = [223, 227, 232];
    const cardBg = [250, 250, 252];

    // Helper function to add header to current page
    const addHeader = async () => {
      // Draw top border
      pdf.setDrawColor(...plumMain);
      pdf.setLineWidth(0.5);
      pdf.line(margin, margin + 8, pageWidth - margin, margin + 8);

      // Try to add logo image
      try {
        const logoImg = new Image();
        logoImg.src = '/logo/logo-full.png';

        if (!logoImg.complete) {
          await new Promise((resolve, reject) => {
            logoImg.onload = resolve;
            logoImg.onerror = () => {
              pdf.setFontSize(11);
              pdf.setFont('helvetica', 'bold');
              pdf.setTextColor(...plumMain);
              pdf.text('CARO Technology', margin, margin + 5);
              resolve();
            };
            setTimeout(reject, 1000);
          });
        }

        if (logoImg.complete && logoImg.naturalHeight !== 0) {
          const logoHeight = 6;
          const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
          pdf.addImage(logoImg, 'PNG', margin, margin, logoWidth, logoHeight);
        } else {
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...plumMain);
          pdf.text('CARO Technology', margin, margin + 5);
        }
      } catch (err) {
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...plumMain);
        pdf.text('CARO Technology', margin, margin + 5);
      }
    };

    // Helper function to add footer to current page
    const addFooter = (pageNum, totalPages) => {
      const footerY = pageHeight - footerHeight + 5;

      // Draw bottom border
      pdf.setDrawColor(...grey300);
      pdf.setLineWidth(0.3);
      pdf.line(margin, footerY, pageWidth - margin, footerY);

      // Add copyright
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...grey500);
      pdf.text(`© ${new Date().getFullYear()} CARO Technology`, margin, footerY + 5);

      // Add page number
      pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth - margin - 25, footerY + 5);
    };

    // Helper function to check page break
    const checkPageBreak = async (requiredSpace = 10) => {
      if (yPosition + requiredSpace > contentAreaBottom) {
        pdf.addPage();
        await addHeader();
        yPosition = contentAreaTop;
        return true;
      }
      return false;
    };

    // Add header to first page
    await addHeader();

    // Document title section
    pdf.setTextColor(...plumMain);
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Tickets To-Do List', margin, yPosition);
    yPosition += 8;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...grey500);
    pdf.text('CARO Technology - Ticket Management', margin, yPosition);
    yPosition += 5;

    pdf.setFontSize(8);
    pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, yPosition);
    yPosition += 3;

    // Divider line
    pdf.setFillColor(...plumMain);
    pdf.rect(margin, yPosition, contentWidth, 0.5, 'F');
    yPosition += 8;

    // Filter info & count
    pdf.setFontSize(9);
    pdf.setTextColor(...grey800);
    const activeFilters = [];
    if (filter.status.length > 0) activeFilters.push(`Status: ${filter.status.join(', ')}`);
    if (filter.urgency) activeFilters.push(`Urgency: ${filter.urgency}`);
    if (filter.support_type) activeFilters.push(`Type: ${filter.support_type}`);
    if (filter.search) activeFilters.push(`Search: ${filter.search}`);

    if (activeFilters.length > 0) {
      pdf.setFont('helvetica', 'italic');
      pdf.text(`Filters: ${activeFilters.join(' | ')}`, margin, yPosition);
      yPosition += 5;
    }

    pdf.setFont('helvetica', 'bold');
    pdf.text(`Total: ${paginatedTickets.length} ticket${paginatedTickets.length !== 1 ? 's' : ''}`, margin, yPosition);
    yPosition += 10;

    // Draw each ticket as a card
    for (const ticket of paginatedTickets) {
      // Get ticket data
      const locationId = ticket.locationId || ticket.location?.id;
      const site = findSiteForLocation(locationId);
      const siteName = site?.friendlyName || site?.name || '-';
      const locationName = ticket.location?.friendlyName || ticket.location?.name || '-';
      const status = ticket.status || '-';
      const urgency = ticket.urgency || '-';
      const title = ticket.title || 'No title';
      const description = ticket.description || '';

      // Calculate card height based on content
      pdf.setFontSize(8);
      const descriptionLines = description ? pdf.splitTextToSize(description, contentWidth - 10) : [];
      const cardHeaderHeight = 28; // Increased for two-line meta info
      const descriptionHeight = descriptionLines.length > 0 ? (descriptionLines.length * 4) + 8 : 0;
      const cardHeight = cardHeaderHeight + descriptionHeight + 4;

      // Check if we need a new page
      await checkPageBreak(cardHeight + 5);

      // Card background
      pdf.setFillColor(...cardBg);
      pdf.setDrawColor(...grey300);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(margin, yPosition, contentWidth, cardHeight, 2, 2, 'FD');

      // Card header background (darker)
      pdf.setFillColor(245, 243, 244);
      pdf.rect(margin + 0.15, yPosition + 0.15, contentWidth - 0.3, cardHeaderHeight - 0.15, 'F');

      // Header separator line
      pdf.setDrawColor(...grey300);
      pdf.line(margin, yPosition + cardHeaderHeight, margin + contentWidth, yPosition + cardHeaderHeight);

      // Ticket ID (plum color)
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...plumMain);
      pdf.text(`#${ticket.id}`, margin + 4, yPosition + 6);

      // Title
      pdf.setTextColor(...grey800);
      const titleX = margin + 18;
      const titleText = title.length > 55 ? title.substring(0, 52) + '...' : title;
      pdf.text(titleText, titleX, yPosition + 6);

      // Row 2: Site (full name on its own line)
      pdf.setFontSize(8);
      const metaY1 = yPosition + 13;

      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...grey500);
      pdf.text('Site:', margin + 4, metaY1);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...plumMain);
      // Full site name - truncate only if very long
      const siteText = siteName.length > 70 ? siteName.substring(0, 67) + '...' : siteName;
      pdf.text(siteText, margin + 14, metaY1);

      // Row 3: Location | Status | Urgency
      const metaY2 = yPosition + 20;
      let metaX = margin + 4;

      // Location
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...grey500);
      pdf.text('Location:', metaX, metaY2);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...grey800);
      metaX += 16;
      const locText = locationName.length > 25 ? locationName.substring(0, 22) + '...' : locationName;
      pdf.text(locText, metaX, metaY2);
      metaX += pdf.getTextWidth(locText) + 8;

      // Status with color
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...grey500);
      pdf.text('Status:', metaX, metaY2);
      metaX += 12;
      if (status === 'Open') pdf.setTextColor(25, 118, 210);
      else if (status === 'In Progress') pdf.setTextColor(237, 108, 2);
      else if (status === 'Resolved' || status === 'Closed') pdf.setTextColor(46, 125, 50);
      else pdf.setTextColor(...grey800);
      pdf.setFont('helvetica', 'normal');
      pdf.text(status, metaX, metaY2);
      metaX += pdf.getTextWidth(status) + 8;

      // Urgency with color
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...grey500);
      pdf.text('Urgency:', metaX, metaY2);
      metaX += 14;
      if (urgency === 'HIGH') pdf.setTextColor(211, 47, 47);
      else if (urgency === 'MEDIUM') pdf.setTextColor(237, 108, 2);
      else if (urgency === 'LOW') pdf.setTextColor(25, 118, 210);
      else pdf.setTextColor(...grey800);
      pdf.setFont('helvetica', 'normal');
      pdf.text(urgency, metaX, metaY2);

      // Description (full text in card body)
      if (descriptionLines.length > 0) {
        pdf.setTextColor(...grey800);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        let descY = yPosition + cardHeaderHeight + 6;
        descriptionLines.forEach((line) => {
          pdf.text(line, margin + 5, descY);
          descY += 4;
        });
      }

      yPosition += cardHeight + 5;
    }

    // Add footers to all pages
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      addFooter(i, totalPages);
    }

    // Save the PDF with formatted name: YYYYMMDD Caro Ticket To do List [SearchTerm]
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const searchTerm = filter.search ? ` ${filter.search.replace(/[^a-zA-Z0-9\s-]/g, '')}` : '';
    const fileName = `${dateStr} Caro Ticket To do List${searchTerm}.pdf`;
    pdf.save(fileName);

    enqueueSnackbar(t ? t('Tickets.pdf_exported') : 'PDF exported successfully', { variant: 'success' });
  };

  // Render functions
  const renderUrgencyChip = (urgency) => {
    const { t } = useThemeSettings();
    let color = 'default';
    switch (urgency) {
      case 'LOW':
        color = 'info';
        break;
      case 'MEDIUM':
        color = 'warning';
        break;
      case 'HIGH':
        color = 'error';
        break;
      case 'URGENT':
        color = 'error';
        break;
      default:
        color = 'default';
    }
    return <Chip label={urgency} color={color} size="small" />;
  };

  const renderStatusChip = (status) => {
    const { t } = useThemeSettings();
    let color = 'default';
    let icon = null;

    switch (status) {
      case 'Open':
        color = 'info';
        icon = <InfoIcon fontSize="small" />;
        break;
      case 'In Progress':
        color = 'warning';
        icon = <TimelineIcon fontSize="small" />;
        break;
      case 'Closed':
        color = 'success';
        icon = <CheckIcon fontSize="small" />;
        break;
      default:
        color = 'default';
    }

    return (
      <Chip
        icon={icon}
        label={status}
        color={color}
        size="small"
        variant={status === 'Closed' ? 'outlined' : 'filled'} />);


  };

  const renderSupportTypeChip = (type) => {
    const { t } = useThemeSettings();
    let label = type;

    switch (type) {
      case 'domotics':
        label = 'Domotics';
        break;
      case 'access_control':
        label = 'Access Control';
        break;
      case 'tablet':
        label = 'Tablet';
        break;
      case 'emergency_system':
        label = 'Emergency System';
        break;
      case 'it_pc':
        label = 'IT/PC';
        break;
      case 'other':
        label = 'Other';
        break;
    }

    return <Chip label={label} size="small" variant="outlined" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Calculate time difference in user-friendly format
  const getTimeDifference = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Unknown';
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      return `${Math.floor(diffDays / 7)} weeks ago`;
    } else {
      return `${Math.floor(diffDays / 30)} months ago`;
    }
  };

  const renderMobileTicketsView = () =>
    <Box>
      {paginatedTickets.map((ticket) =>
        <Card key={ticket.id} className="card-animated card-enter" sx={{ mb: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">{t ? t("Tickets.ticket_id") : "Ticket ID"}

                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  #{ticket.id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t ? t("VisualTreeView.title") : "Title"}

                </Typography>
                <Typography variant="body1">
                  {ticket.title || (ticket.description ? ticket.description.substring(0, 50) + '...' : 'No title')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {renderStatusChip(ticket.status)}
                {renderUrgencyChip(ticket.urgency)}
                {renderSupportTypeChip(ticket.support_type)}
              </Box>
              {ticket.next_step &&
                <Box>
                  <Typography variant="caption" color="text.secondary">{t ? t("VisualTreeView.next_step") : "Next Step"}

                  </Typography>
                  <Typography variant="body2">
                    {ticket.next_step}
                  </Typography>
                </Box>
              }
              {ticket.location?.friendlyName &&
                <Box>
                  <Typography variant="caption" color="text.secondary">{t ? t("Tickets.location") : "Location"}

                  </Typography>
                  <Typography
                    variant="body2"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/locations?selectedId=${ticket.locationId || ticket.location.id}`);
                    }}
                    sx={{
                      cursor: 'pointer',
                      color: 'primary.main',
                      textDecoration: 'underline',
                      '&:hover': { color: 'primary.dark' }
                    }}>

                    {ticket.location.friendlyName}
                  </Typography>
                </Box>
              }
              {(ticket.related_device_name || ticket.related_device_id) &&
                <Box>
                  <Typography variant="caption" color="text.secondary">{t ? t("Tickets.device") : "Device"}

                  </Typography>
                  <Typography
                    variant="body2"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/devices?selectedId=${ticket.related_device_id}`);
                    }}
                    sx={{
                      cursor: 'pointer',
                      color: 'primary.main',
                      textDecoration: 'underline',
                      '&:hover': { color: 'primary.dark' }
                    }}>

                    {ticket.related_device_name || `Device #${ticket.related_device_id}`}
                  </Typography>
                </Box>
              }
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t ? t("LocationDetails.created") : "Created"}

                  </Typography>
                  <Typography variant="body2">
                    {getTimeDifference(ticket.created_at)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t ? t("Tickets.last_update") : "Last Update"}

                  </Typography>
                  <Typography variant="body2">
                    {getTimeDifference(ticket.updated_at)}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </CardContent>
          <Divider />
          <CardActions>
            <Button
              startIcon={<InfoIcon />}
              onClick={() => handleTicketSelect(ticket)}
              color="primary"
              size="small">{t ? t("LocationManagement.view_details") : "View Details"}


            </Button>
          </CardActions>
        </Card>
      )}
      {paginatedTickets.length === 0 &&
        <Paper className="card-animated card-enter" sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary" align="center">{t ? t("Tickets.no_tickets_found_adjust_your_filters") : "No tickets found. Adjust your filters or create a new ticket."}

          </Typography>
        </Paper>
      }
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <TablePagination
          component="div"
          count={totalTickets}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100, { label: t ? t('Common.all') : 'All', value: -1 }]}
          labelRowsPerPage={t ? t('Common.rows_per_page') : 'Rows per page:'} />

      </Box>
    </Box>;


  // Render content based on view mode
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <div className="spinner" />
      </Box>);

  }

  if (error) {
    return (
      <Alert severity="error" className="slide-up" sx={{ mb: 3 }}>
        {error}
      </Alert>);

  }

  // Ticket List View
  if (viewMode === 'list') {
    return (
      <Box
        className="page-transition-enter"
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box'
        }}>

        {/* Header */}
        <Box sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0
        }}>
          <Typography variant={isMobile ? "h5" : "h4"} className="slide-down">{t ? t("Tickets.tickets") : "Tickets"}</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTicket}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "medium"}>{t ? t("TicketsTable.create_ticket") : "Create Ticket"}


          </Button>
        </Box>

        {/* Search and Filter Bar */}
        <Paper className="card-animated" sx={{ mb: 3, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  placeholder={t ? t("TicketsTable.search_tickets") : "Search by ID, title, description, location, or next step..."}
                  variant="outlined"
                  value={searchInputValue}
                  onChange={(e) => setSearchInputValue(e.target.value)}
                  onBlur={() => {
                    // Immediately trigger search when user leaves the field
                    if (searchDebounceRef.current) {
                      clearTimeout(searchDebounceRef.current);
                    }
                    if (searchInputValue !== searchFilter) {
                      setSearchFilter(searchInputValue);
                      setPage(0);
                    }
                  }}
                  InputProps={{
                    startAdornment:
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>

                  }} />
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {t ? t("Tickets.search_tips") : "Search Tips:"}
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                        • {t ? t("Tickets.search_tip_normal") : "Normal: tablet (finds 'tablet' anywhere)"}
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                        • {t ? t("Tickets.search_tip_wildcard_start") : "Wildcard: SH14*01 (starts with SH14, ends with 01)"}
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                        • {t ? t("Tickets.search_tip_wildcard_multi") : "Multiple: SH14*01* (starts with SH14, has 01 anywhere after)"}
                      </Typography>
                      <Typography variant="caption" component="div">
                        • {t ? t("Tickets.search_tip_wildcard_any") : "*printer* (has 'printer' anywhere in text)"}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="right"
                >
                  <IconButton size="small" sx={{ color: 'primary.main' }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1, flexDirection: { xs: 'column', md: 'row' } }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setFilterDrawerOpen(true)}
                fullWidth={isMobile}>{t ? t("Tickets.filter") : "Filter"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={handleExportPdf}
                fullWidth={isMobile}>{t ? t("Tickets.export_pdf") : "Export PDF"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  fetchTickets();
                }}
                fullWidth={isMobile}>{t ? t("DeviceStatusDashboard.refresh") : "Refresh"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Filter Chips (if filters are active) */}
        {(filter.status.length > 0 || filter.urgency || filter.support_type || filter.next_step) &&
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {filter.status.length > 0 && filter.status.map((status) => (
              <Chip
                key={status}
                label={`Status: ${status}`}
                onDelete={() => handleFilterChange('status', filter.status.filter(s => s !== status))}
                color="primary"
                variant="outlined" />

            ))}
            {filter.urgency &&
              <Chip
                label={`Urgency: ${filter.urgency}`}
                onDelete={() => handleFilterChange('urgency', '')}
                color="primary"
                variant="outlined" />

            }
            {filter.support_type &&
              <Chip
                label={`Type: ${filter.support_type.replace('_', ' ').toUpperCase()}`}
                onDelete={() => handleFilterChange('support_type', '')}
                color="primary"
                variant="outlined" />

            }
            {filter.next_step &&
              <Chip
                label={`Next Step: ${filter.next_step}`}
                onDelete={() => handleFilterChange('next_step', '')}
                color="primary"
                variant="outlined" />

            }
            <Chip
              label={t ? t("Tickets.clear_all_filters") : "Clear All Filters"}
              onClick={handleResetFilter}
              color="secondary" />

          </Box>
        }

        {/* Tickets Table */}
        {isMobile ?
          renderMobileTicketsView() :

          <TableContainer component={Paper}>
            <Table aria-label={t ? t("TicketsTable.tickets_table") : "tickets table"}>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={orderBy === 'id' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}>
                      ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'title' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'title'}
                      direction={orderBy === 'title' ? order : 'asc'}
                      onClick={() => handleRequestSort('title')}>
                      {t ? t("VisualTreeView.title") : "Title"}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'status' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={() => handleRequestSort('status')}>
                      {t ? t("DeviceTable.status") : "Status"}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'urgency' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'urgency'}
                      direction={orderBy === 'urgency' ? order : 'asc'}
                      onClick={() => handleRequestSort('urgency')}>
                      {t ? t("VisualTreeView.urgency") : "Urgency"}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'support_type' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'support_type'}
                      direction={orderBy === 'support_type' ? order : 'asc'}
                      onClick={() => handleRequestSort('support_type')}>
                      {t ? t("DeviceTable.type") : "Type"}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'next_step' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'next_step'}
                      direction={orderBy === 'next_step' ? order : 'asc'}
                      onClick={() => handleRequestSort('next_step')}>
                      {t ? t("VisualTreeView.next_step") : "Next Step"}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'location' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'location'}
                      direction={orderBy === 'location' ? order : 'asc'}
                      onClick={() => handleRequestSort('location')}>
                      {t ? t("Tickets.location") : "Location"}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'device' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'device'}
                      direction={orderBy === 'device' ? order : 'asc'}
                      onClick={() => handleRequestSort('device')}>
                      {t ? t("Tickets.device") : "Device"}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'createdAt' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'createdAt'}
                      direction={orderBy === 'createdAt' ? order : 'asc'}
                      onClick={() => handleRequestSort('createdAt')}>
                      {t ? t("LocationDetails.created") : "Created"}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'updatedAt' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'updatedAt'}
                      direction={orderBy === 'updatedAt' ? order : 'asc'}
                      onClick={() => handleRequestSort('updatedAt')}>
                      {t ? t("Tickets.last_update") : "Last Update"}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>{t ? t("DeviceTable.actions") : "Actions"}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTickets.length > 0 ?
                  paginatedTickets.map((ticket) =>
                    <TableRow key={ticket.id} hover onClick={() => handleTicketSelect(ticket)} sx={{ cursor: 'pointer' }}>
                      <TableCell>#{ticket.id}</TableCell>
                      <TableCell>{ticket.title || (ticket.description ? ticket.description.substring(0, 30) + '...' : 'No title')}</TableCell>
                      <TableCell>{renderStatusChip(ticket.status)}</TableCell>
                      <TableCell>{renderUrgencyChip(ticket.urgency)}</TableCell>
                      <TableCell>{renderSupportTypeChip(ticket.support_type)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {ticket.next_step || 'Not defined'}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {ticket.location?.friendlyName ?
                          <Tooltip title={t ? t("Tickets.click_view_location") : "Click to view location"}>
                            <Box
                              component="span"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/locations?selectedId=${ticket.locationId || ticket.location.id}`);
                              }}
                              sx={{
                                cursor: 'pointer',
                                color: 'primary.main',
                                textDecoration: 'underline',
                                '&:hover': { color: 'primary.dark' }
                              }}>

                              {ticket.location.friendlyName}
                            </Box>
                          </Tooltip> :

                          'N/A'
                        }
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {ticket.related_device_name || ticket.related_device_id ?
                          <Tooltip title={t ? t("Tickets.click_view_device") : "Click to view device"}>
                            <Box
                              component="span"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/devices?selectedId=${ticket.related_device_id}`);
                              }}
                              sx={{
                                cursor: 'pointer',
                                color: 'primary.main',
                                textDecoration: 'underline',
                                '&:hover': { color: 'primary.dark' }
                              }}>

                              {ticket.related_device_name || `Device #${ticket.related_device_id}`}
                            </Box>
                          </Tooltip> :

                          'N/A'
                        }
                      </TableCell>
                      <TableCell>{getTimeDifference(ticket.created_at)}</TableCell>
                      <TableCell>{getTimeDifference(ticket.updated_at)}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTicketSelect(ticket);
                          }}>

                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ) :

                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body1" sx={{ py: 2 }}>{t ? t("Tickets.no_tickets_found_adjust_your_filters") : "No tickets found. Adjust your filters or create a new ticket."}

                      </Typography>
                    </TableCell>
                  </TableRow>
                }
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalTickets}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100, { label: t ? t('Common.all') : 'All', value: -1 }]}
              labelRowsPerPage={t ? t('Common.rows_per_page') : 'Rows per page:'} />

          </TableContainer>
        }

        {/* Filter Drawer */}
        <Drawer
          anchor="right"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}>

          <Box sx={{ width: 300, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t ? t("Tickets.filter_tickets") : "Filter Tickets"}</Typography>
              <IconButton onClick={() => setFilterDrawerOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t ? t("DeviceTable.status") : "Status"}</InputLabel>
              <Select
                multiple
                value={filter.status}
                label={t ? t("DeviceTable.status") : "Status"}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                renderValue={(selected) => selected.join(', ')}>

                <MenuItem value="Open">{t ? t("VisualTreeView.open") : "Open"}</MenuItem>
                <MenuItem value="In Progress">{t ? t("VisualTreeView.progress") : "In Progress"}</MenuItem>
                <MenuItem value="Closed">{t ? t("VisualTreeView.closed") : "Closed"}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t ? t("VisualTreeView.urgency") : "Urgency"}</InputLabel>
              <Select
                value={filter.urgency}
                label={t ? t("VisualTreeView.urgency") : "Urgency"}
                onChange={(e) => handleFilterChange('urgency', e.target.value)}>

                <MenuItem value="">{t ? t("LocationManagement.all") : "All"}</MenuItem>
                <MenuItem value="LOW">{t ? t("VisualTreeView.low") : "Low"}</MenuItem>
                <MenuItem value="MEDIUM">{t ? t("VisualTreeView.medium") : "Medium"}</MenuItem>
                <MenuItem value="HIGH">{t ? t("VisualTreeView.high") : "High"}</MenuItem>
                <MenuItem value="URGENT">{t ? t("VisualTreeView.urgent") : "Urgent"}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t ? t("VisualTreeView.support_type") : "Support Type"}</InputLabel>
              <Select
                value={filter.support_type}
                label={t ? t("VisualTreeView.support_type") : "Support Type"}
                onChange={(e) => handleFilterChange('support_type', e.target.value)}>

                <MenuItem value="">{t ? t("LocationManagement.all") : "All"}</MenuItem>
                <MenuItem value="domotics">{t ? t("VisualTreeView.domotics") : "Domotics"}</MenuItem>
                <MenuItem value="access_control">{t ? t("VisualTreeView.access_control") : "Access Control"}</MenuItem>
                <MenuItem value="tablet">{t ? t("VisualTreeView.tablet") : "Tablet"}</MenuItem>
                <MenuItem value="emergency_system">{t ? t("VisualTreeView.emergency_system") : "Emergency System"}</MenuItem>
                <MenuItem value="it_pc">{t ? t("VisualTreeView.itpc") : "IT/PC"}</MenuItem>
                <MenuItem value="other">{t ? t("VisualTreeView.other") : "Other"}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={t ? t("Tickets.next_action_step") : "Next Action Step"}
              placeholder={t ? t("Tickets.filter_next_step") : "Filter by next step..."}
              value={filter.next_step}
              onChange={(e) => handleFilterChange('next_step', e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: filter.next_step && (
                  <IconButton size="small" onClick={() => handleFilterChange('next_step', '')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleResetFilter}>{t ? t("Tickets.reset_filters") : "Reset Filters"}


              </Button>
              <Button
                variant="contained"
                onClick={() => setFilterDrawerOpen(false)}>{t ? t("SolutionRecommendation.apply") : "Apply"}


              </Button>
            </Box>
          </Box>
        </Drawer>
      </Box>);

  }

  // Create Ticket View
  if (viewMode === 'create') {
    return (
      <TicketCreateView
        onClose={() => setViewMode('list')}
        onSuccess={(createdTicket) => {
          // Refresh tickets list
          fetchTickets();
          // Navigate to the new ticket
          setSelectedTicket(createdTicket);
          setViewMode('view');
        }}
        embedded={false} />);

  }

  // View Ticket
  if (viewMode === 'view' && selectedTicket) {
    return (
      <Box
        className="page-transition-enter"
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box'
        }}>

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={handleBackToList} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" className="slide-down">{t ? t("VisualTreeView.ticket") : "Ticket #"}{selectedTicket.id}</Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditTicket}
              sx={{ mr: 1 }}>{t ? t("DeviceTable.edit") : "Edit"}


            </Button>
            {isSupport &&
              <>
                <Button
                  variant="outlined"
                  startIcon={<BookmarkAddIcon />}
                  onClick={() => setSaveAsKnownIssueOpen(true)}
                  sx={{ mr: 1 }}>{t ? t("SaveAsKnownIssue.save_as_known_issue") : "Save as Known Issue"}


                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SolutionIcon />}
                  onClick={() => setSelectSolutionOpen(true)}
                  sx={{ mr: 1 }}>{t ? t("Tickets.apply_solution") : "Apply Solution"}


                </Button>
              </>
            }
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteTicket}>{t ? t("DeviceTable.delete") : "Delete"}


            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Ticket Details */}
          <Grid item xs={12} md={7} lg={8}>
            <Paper className="card-animated card-enter" sx={{ p: 3, mb: 3 }}>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="h5" component="h2" gutterBottom>
                  {selectedTicket.title || 'No title provided'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {renderStatusChip(selectedTicket.status)}
                  <Tooltip title={isFollowing ? 'Unfollow ticket - Stop receiving notifications' : 'Follow ticket - Get notified of updates'}>
                    <Button
                      variant={isFollowing ? 'contained' : 'outlined'}
                      color={isFollowing ? 'primary' : 'inherit'}
                      startIcon={isFollowing ? <FollowingIcon /> : <NotFollowingIcon />}
                      onClick={toggleFollow}
                      disabled={loadingFollow}
                      size="small">

                      {loadingFollow ? 'Loading...' : isFollowing ? 'Following' : 'Follow'}
                      {followersCount > 0 && ` (${followersCount})`}
                    </Button>
                  </Tooltip>
                  {linkedIntervention && (
                    <>
                      {/* Only show if support user */}
                      {isSupport && (
                        <>
                          <Tooltip title={linkedInterventions.length > 1 ? "Select an intervention report to view" : "View Field Intervention Report"}>
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<AssignmentIcon />}
                              onClick={handleViewReportClick}
                              size="small"
                            >
                              {t ? t("Tickets.view_intervention") : "View Report"}
                              {linkedInterventions.length > 1 && ` (${linkedInterventions.length})`}
                            </Button>
                          </Tooltip>
                        </>
                      )}

                      {/* Show reschedule/cancel buttons only for latest intervention and if it's not already cancelled/completed */}
                      {isSupport && linkedIntervention.status !== 'cancelled' && linkedIntervention.status !== 'completed' && (
                        <>
                          <Tooltip title={t ? t("Tickets.reschedule_this_intervention") : "Reschedule this intervention"}>
                            <Button
                              variant="contained"
                              color="warning"
                              startIcon={<EventIcon />}
                              onClick={handleRescheduleButtonClick}
                              size="small"
                            >
                              {t ? t("Tickets.reschedule") : "Reschedule"}
                            </Button>
                          </Tooltip>

                          <Tooltip title={t ? t("Tickets.cancel_this_intervention") : "Cancel this intervention"}>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={handleCancelButtonClick}
                              size="small"
                            >
                              {t ? t("Common.cancel") : "Cancel"}
                            </Button>
                          </Tooltip>
                        </>
                      )}
                    </>
                  )}
                  {isSupport && (!linkedIntervention || linkedIntervention.status === 'cancelled' || linkedIntervention.status === 'completed') &&
                    <Tooltip title={t ? t("Tickets.request_physical_intervention_this_t") : "Request physical intervention for this ticket"}>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<BuildIcon />}
                        onClick={handleInterventionButtonClick}
                        size="small">{t ? t("Tickets.intervention") : "Intervention"}


                      </Button>
                    </Tooltip>
                  }
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("LocationDialog.description") : "Description"}

                </Typography>
                <Typography variant="body1" paragraph>
                  {selectedTicket.description}
                </Typography>
              </Box>

              {selectedTicket.steps_tried &&
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("VisualTreeView.steps_already_tried") : "Steps Already Tried"}

                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedTicket.steps_tried}
                  </Typography>
                </Box>
              }

              {/* Solution Recommendations - Show to support staff */}
              {isSupport && selectedTicket.description &&
                <Box sx={{ mb: 3 }}>
                  <SolutionRecommendation
                    ticketDescription={selectedTicket.description}
                    isSupport={isSupport}
                    onSolutionSelect={(solution) => {
                      //console.log('Selected solution:', solution);
                      // Handle solution selection - could apply it to the ticket
                    }} />

                </Box>
              }

              <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab label={t ? t("Tickets.messages") : "Messages"} />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{t ? t("SaveAsKnownIssue.attachments") : "Attachments"}</span>
                      {selectedTicket?.TicketAttachments && (() => {
                        const standaloneCount = selectedTicket.TicketAttachments.filter(a => !a.messageId && !a.message_id).length;
                        return standaloneCount > 0 ? (
                          <Chip
                            label={standaloneCount}
                            size="small"
                            color="primary"
                            sx={{ height: 20, fontSize: '0.75rem' }}
                          />
                        ) : null;
                      })()}
                    </Box>
                  }
                />
                <Tab label={t ? t("Tickets.activity") : "Activity"} />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <Box sx={{ mb: 3 }}>
                  {ticketMessages.length > 0 ?
                    <Box>
                      {ticketMessages.map((message) =>
                        <Box
                          key={message.id}
                          sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: 1,
                            bgcolor: message.is_system_message ?
                              alpha(theme.palette.info.main, 0.1) :
                              message.userId === 101 // Current user ID
                                ? alpha(theme.palette.primary.main, 0.1) :
                                alpha(theme.palette.grey[100], 0.5),
                            borderLeft: message.is_system_message ?
                              `4px solid ${theme.palette.info.main}` :
                              message.userId === 101 // Current user ID
                                ? `4px solid ${theme.palette.primary.main}` :
                                'none'
                          }}>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" component="span">
                              {message.is_system_message ?
                                'System' :
                                message.User?.username}

                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(message.created_at)}
                            </Typography>
                          </Box>
                          <Typography variant="body1">
                            {message.message}
                          </Typography>

                          {/* Display intervention date if this is an intervention request */}
                          {message.is_system_message && message.message?.includes('Physical intervention requested') &&
                            <Box sx={{ mt: 1, p: 1, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 1 }}>
                              {/* Try to extract date from message content or use a generic intervention notice */}
                              {interventionDate &&
                                <Typography variant="body2" color="warning.main" fontWeight="medium">{t ? t("Tickets.scheduled") : "\uD83D\uDCC5 Scheduled for:"}
                                  {new Date(interventionDate).toLocaleDateString('en-GB', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </Typography>
                              }
                              {/* Fallback if no specific date is stored */}
                              {!interventionDate &&
                                <Typography variant="body2" color="warning.main" fontWeight="medium">{t ? t("Tickets.physical_intervention_has_been_requeste") : "\uD83D\uDD27 Physical intervention has been requested for this ticket"}

                                </Typography>
                              }
                            </Box>
                          }

                          {/* Also check if ticket has intervention date stored */}
                          {selectedTicket?.intervention_date && message.is_system_message && message.message?.includes('Physical intervention') &&
                            <Box sx={{ mt: 1, p: 1, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 1 }}>
                              <Typography variant="body2" color="warning.main" fontWeight="medium">{t ? t("Tickets.scheduled") : "\uD83D\uDCC5 Scheduled for:"}
                                {new Date(selectedTicket.intervention_date).toLocaleDateString('en-GB', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </Typography>
                            </Box>
                          }

                          {/* Display attachments */}
                          {message.TicketAttachments && message.TicketAttachments.length > 0 &&
                            <Box sx={{ mt: 2 }}>
                              {message.TicketAttachments.map((attachment) => {
                                const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
                                const fileUrl = attachment.file_id
                                  ? `${API_BASE_URL}/api/files/${attachment.file_id}/download?inline=true`
                                  : `https://files.caro.care${attachment.url}`;
                                const downloadUrl = attachment.file_id
                                  ? `${API_BASE_URL}/api/files/${attachment.file_id}/download?inline=false`
                                  : `https://files.caro.care${attachment.url}`;

                                return (
                                  <Chip
                                    key={attachment.id}
                                    icon={<FileIcon />}
                                    label={attachment.originalName}
                                    onClick={async () => {
                                      try {
                                        const token = await getTokenFromStorage('auth_token');
                                        const response = await fetch(downloadUrl, {
                                          headers: { 'Authorization': `Bearer ${token}` }
                                        });

                                        if (!response.ok) throw new Error('Download failed');

                                        const blob = await response.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = attachment.originalName || 'download';
                                        document.body.appendChild(a);
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                        document.body.removeChild(a);
                                      } catch (error) {
                                        console.error('Download error:', error);
                                        enqueueSnackbar('Failed to download file', { variant: 'error' });
                                      }
                                    }}
                                    sx={{ mr: 1, mb: 1, cursor: 'pointer' }}
                                    variant="outlined"
                                    color="primary" />
                                );
                              })}
                            </Box>
                          }
                        </Box>
                      )}
                    </Box> :

                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>{t ? t("Tickets.no_messages_yet") : "No messages yet."}

                    </Typography>
                  }
                </Box>

                {/* Selected files display */}
                {selectedFiles.length > 0 &&
                  <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedFiles.map((file, index) =>
                      <Chip
                        key={index}
                        icon={<FileIcon />}
                        label={file.name}
                        onDelete={() => handleRemoveFile(index)}
                        size="small"
                        variant="outlined" />

                    )}
                  </Box>
                }

                <Box sx={{ display: 'flex', mb: 2, gap: 1 }}>
                  <input
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    id="file-upload-input"
                    onChange={handleFileSelect} />

                  <label htmlFor="file-upload-input">
                    <IconButton
                      component="span"
                      color="primary"
                      disabled={uploadingFiles}>

                      <AttachFileIcon />
                    </IconButton>
                  </label>

                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={t ? t("Tickets.type_your_message") : "Type your message..."}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    multiline
                    maxRows={4}
                    disabled={uploadingFiles} />


                  <Button
                    variant="contained"
                    startIcon={uploadingFiles ? <CircularProgress size={20} /> : <SendIcon />}
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() && selectedFiles.length === 0 || uploadingFiles}
                    sx={{ minWidth: '100px' }}>

                    {uploadingFiles ? 'Sending...' : 'Send'}
                  </Button>
                </Box>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                {/* Attachments Tab */}
                <Box sx={{ py: 2 }}>
                  {/* Intervention Reports Section */}
                  {linkedInterventions && linkedInterventions.filter(i => i.status === 'completed').length > 0 && (
                    <>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        {t ? t("Tickets.intervention_reports") : "Field Intervention Reports"}
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 4 }}>
                        {linkedInterventions
                          .filter(intervention => intervention.status === 'completed')
                          .map((intervention) => (
                            <Grid item xs={12} sm={6} md={4} key={intervention.id}>
                              <Card
                                className="card-animated card-enter"
                                variant="outlined"
                                sx={{ height: '100%' }}
                              >
                                <CardContent>
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <AssignmentIcon color="success" sx={{ mt: 0.5 }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 500,
                                          mb: 0.5
                                        }}
                                      >
                                        {`Intervention Report #${intervention.id}`}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {t ? t("Tickets.date") : "Date:"} {intervention.intervention_date ? new Date(intervention.intervention_date).toLocaleDateString() : 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {t ? t("Tickets.technician") : "Technician:"} {intervention.technician_name || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {t ? t("Tickets.outcome") : "Outcome:"} {
                                          intervention.outcome === 'included_in_contract'
                                            ? (t ? t("FieldInterventions.outcome_included_in_contract") : "Included in Maintenance Contract")
                                            : intervention.outcome === 'not_included'
                                              ? (t ? t("FieldInterventions.outcome_not_included") : "Not Included in Contract")
                                              : intervention.outcome === 'cancelled'
                                                ? (t ? t("FieldInterventions.outcome_cancelled") : "Cancelled")
                                                : (intervention.outcome || 'N/A')
                                        }
                                      </Typography>
                                      <Chip
                                        label={t ? t("Tickets.completed") : "Completed"}
                                        size="small"
                                        color="success"
                                        sx={{ mt: 1 }}
                                      />
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="success"
                                      startIcon={<DownloadIcon />}
                                      onClick={async () => {
                                        try {
                                          const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
                                          const token = await getTokenFromStorage('auth_token');
                                          const response = await fetch(`${API_URL}/api/field-interventions/${intervention.id}/pdf`, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          });

                                          if (response.ok) {
                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = `intervention-report-${intervention.id}.pdf`;
                                            document.body.appendChild(a);
                                            a.click();
                                            window.URL.revokeObjectURL(url);
                                            document.body.removeChild(a);
                                            enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
                                          } else {
                                            throw new Error('Failed to download PDF');
                                          }
                                        } catch (error) {
                                          console.error('Error downloading PDF:', error);
                                          enqueueSnackbar('Failed to download PDF', { variant: 'error' });
                                        }
                                      }}
                                      fullWidth
                                    >
                                      {t ? t("Tickets.download_pdf") : "Download PDF"}
                                    </Button>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                      </Grid>
                      <Divider sx={{ my: 3 }} />
                    </>
                  )}

                  <Typography variant="h6" sx={{ mb: 2 }}>{t ? t("Tickets.ticket_attachments") : "Ticket Attachments"}

                  </Typography>

                  {/* Upload new attachments */}
                  <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      {t ? t("Tickets.add_new_attachments") : "Add New Attachments"}
                    </Typography>
                    <TicketAttachmentUpload
                      onFilesUploaded={async (uploadedFiles) => {
                        try {
                          //console.log('Linking uploaded files to ticket:', uploadedFiles);

                          // Link uploaded files to the ticket
                          for (const file of uploadedFiles) {
                            const attachmentData = {
                              file_id: file.file_id,
                              originalName: file.name,
                              size: file.size,
                              mimetype: file.mime_type,
                              url: file.url
                            };

                            //console.log('Sending attachment data:', attachmentData);
                            const response = await ticketsApi.addAttachment(selectedTicket.id, attachmentData);
                            //console.log('Attachment added response:', response);
                          }

                          enqueueSnackbar(
                            t ? t("Tickets.attachments_added_successfully") : "Attachments added successfully",
                            { variant: 'success' }
                          );

                          // Refresh ticket to show new attachments
                          //console.log('Refreshing ticket data...');
                          const refreshedTicket = await ticketsApi.getById(selectedTicket.id);
                          //console.log('Refreshed ticket:', refreshedTicket.data);

                          // Update the selected ticket with fresh data
                          const fullTicketData = refreshedTicket.data.data || refreshedTicket.data;
                          setSelectedTicket(transformTicketData(fullTicketData.ticket || fullTicketData));
                        } catch (error) {
                          console.error('Error linking attachments to ticket:', error);
                          console.error('Error details:', error.response?.data);
                          enqueueSnackbar(
                            error.response?.data?.error || t ? t("Tickets.failed_to_add_attachments") : "Failed to add attachments to ticket",
                            { variant: 'error' }
                          );
                        }
                      }}
                      compact={true}
                    />
                  </Paper>

                  {(() => {
                    //console.log('Rendering Attachments tab');
                    //console.log('selectedTicket:', selectedTicket);
                    //console.log('All TicketAttachments:', selectedTicket?.TicketAttachments);
                    const standaloneAttachments = selectedTicket?.TicketAttachments?.filter(a => !a.messageId && !a.message_id) || [];
                    //console.log('Standalone attachments (messageId=null):', standaloneAttachments);

                    if (standaloneAttachments.length > 0) {
                      return (
                        <Grid container spacing={2}>
                          {standaloneAttachments.map((attachment) => {
                        // Determine file URL based on whether it's from encrypted system or legacy
                        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
                        const fileUrl = attachment.file_id
                          ? `${API_BASE_URL}/api/files/${attachment.file_id}/download?inline=true`
                          : `https://files.caro.care${attachment.url}`;
                        const downloadUrl = attachment.file_id
                          ? `${API_BASE_URL}/api/files/${attachment.file_id}/download?inline=false`
                          : `https://files.caro.care${attachment.url}`;

                        return (
                        <Grid item xs={12} sm={6} md={4} key={attachment.id}>
                          <Card
                            className="card-animated card-enter"
                            variant="outlined"
                            sx={{
                              cursor: 'default'
                            }}>

                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <FileIcon color="primary" sx={{ mt: 0.5 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 500,
                                      wordBreak: 'break-word',
                                      mb: 0.5
                                    }}>

                                    {attachment.originalName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">{t ? t("Tickets.size") : "Size:"}
                                    {(attachment.size / 1024).toFixed(2)} KB
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">{t ? t("DeviceStatusDashboard.type") : "Type:"}
                                    {attachment.mimetype}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">{t ? t("Tickets.uploaded") : "Uploaded:"}
                                    {new Date(attachment.created_at).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<DownloadIcon />}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      // Create a temporary link to trigger download
                                      const token = await getTokenFromStorage('auth_token');
                                      const response = await fetch(downloadUrl, {
                                        headers: {
                                          'Authorization': `Bearer ${token}`
                                        }
                                      });

                                      if (!response.ok) throw new Error('Download failed');

                                      const blob = await response.blob();
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = attachment.originalName || 'download';
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      document.body.removeChild(a);

                                      enqueueSnackbar(
                                        t ? t("Tickets.file_downloaded") : "File downloaded successfully",
                                        { variant: 'success' }
                                      );
                                    } catch (error) {
                                      console.error('Download error:', error);
                                      enqueueSnackbar(
                                        t ? t("Tickets.download_failed") : "Failed to download file",
                                        { variant: 'error' }
                                      );
                                    }
                                  }}
                                  fullWidth>{t ? t("Tickets.download") : "Download"}


                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (window.confirm(t ? t("Tickets.confirm_delete_attachment") : "Are you sure you want to delete this attachment?")) {
                                      try {
                                        await ticketsApi.deleteAttachment(selectedTicket.id, attachment.id);
                                        enqueueSnackbar(
                                          t ? t("Tickets.attachment_deleted") : "Attachment deleted successfully",
                                          { variant: 'success' }
                                        );

                                        // Refresh ticket to update attachments list
                                        const refreshedTicket = await ticketsApi.getById(selectedTicket.id);
                                        const fullTicketData = refreshedTicket.data.data || refreshedTicket.data;
                                        setSelectedTicket(transformTicketData(fullTicketData.ticket || fullTicketData));
                                      } catch (error) {
                                        console.error('Delete error:', error);
                                        enqueueSnackbar(
                                          error.response?.data?.error || t ? t("Tickets.delete_failed") : "Failed to delete attachment",
                                          { variant: 'error' }
                                        );
                                      }
                                    }
                                  }}
                                  fullWidth>{t ? t("Tickets.delete") : "Delete"}
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                            );
                          })}
                        </Grid>
                      );
                    } else {
                      return (
                        <Box
                          className="fade-in"
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 6,
                            color: 'text.secondary'
                          }}>

                          <FileIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                          <Typography variant="h6" gutterBottom>{t ? t("Tickets.no_attachments") : "No Attachments"}

                          </Typography>
                          <Typography variant="body2">{t ? t("Tickets.this_ticket_doesnt_have_any_attachments") : "This ticket doesn't have any attachments yet."}

                          </Typography>
                        </Box>
                      );
                    }
                  })()}
                </Box>
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>{t ? t("Tickets.track_history_actions_taken_th") : "Track the history of actions taken on this ticket."}

                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{
                      display: 'flex',
                      mb: 2,
                      pb: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`
                    }}>
                      <Box sx={{
                        mr: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}>
                        <Box sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          mb: 1
                        }} />
                        <Box sx={{ width: 2, height: 40, bgcolor: 'grey.300' }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">{t ? t("Tickets.ticket_created") : "Ticket created"}

                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(selectedTicket.created_at)}{t ? t("Tickets.text_14") : "by"}{selectedTicket.createdByUser?.name || `${selectedTicket.createdByUser?.firstName || ''} ${selectedTicket.createdByUser?.lastName || ''}`.trim() || 'Unknown'}
                        </Typography>
                      </Box>
                    </Box>

                    {selectedTicket.status !== 'Open' &&
                      <Box sx={{
                        display: 'flex',
                        mb: 2,
                        pb: 2,
                        borderBottom: `1px solid ${theme.palette.divider}`
                      }}>
                        <Box sx={{
                          mr: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}>
                          <Box sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: 'warning.main',
                            mb: 1
                          }} />
                          <Box sx={{ width: 2, height: 40, bgcolor: 'grey.300' }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{t ? t("Tickets.status_changed_progress") : "Status changed to In Progress"}

                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(selectedTicket.updated_at)}{t ? t("Tickets.support_team") : "by Support Team"}
                          </Typography>
                        </Box>
                      </Box>
                    }

                    {selectedTicket.status === 'Closed' &&
                      <Box sx={{
                        display: 'flex',
                        mb: 2
                      }}>
                        <Box sx={{
                          mr: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}>
                          <Box sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: 'success.main'
                          }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{t ? t("Tickets.ticket_closed") : "Ticket closed"}

                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(selectedTicket.updated_at)}{t ? t("Tickets.text_14") : "by"}{selectedTicket.supportUser?.name || `${selectedTicket.supportUser?.firstName || ''} ${selectedTicket.supportUser?.lastName || ''}`.trim() || 'Unknown'}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  </Box>
                </Box>
              </TabPanel>
            </Paper>
          </Grid>

          {/* Ticket Info Sidebar */}
          <Grid item xs={12} md={5} lg={4}>
            <Paper className="card-animated card-enter" sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" component="h3" gutterBottom>{t ? t("Tickets.ticket_information") : "Ticket Information"}

              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("DeviceTable.status") : "Status"}

                </Typography>
                {isSupport ?
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedTicket.status || 'Open'}
                      onChange={async (e) => {
                        try {
                          const newStatus = e.target.value;

                          const response = await ticketsApi.update(selectedTicket.id, { status: newStatus });

                          // Handle different response structures
                          let ticketData = response.data;
                          if (response.data.data) {
                            ticketData = response.data.data;
                          }
                          if (ticketData.ticket) {
                            ticketData = ticketData.ticket;
                          }

                          const updatedTicket = transformTicketData(ticketData);

                          setSelectedTicket(updatedTicket);
                          setTickets(tickets.map((t) => t.id === selectedTicket.id ? updatedTicket : t));

                          enqueueSnackbar('Ticket status updated successfully', { variant: 'success' });
                        } catch (error) {
                          console.error('Error updating ticket status:', error);
                          enqueueSnackbar('Failed to update ticket status', { variant: 'error' });
                        }
                      }}
                      displayEmpty
                      renderValue={(value) =>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {renderStatusChip(value)}
                        </Box>
                      }>

                      <MenuItem value="Open">
                        {renderStatusChip('Open')}
                      </MenuItem>
                      <MenuItem value="In Progress">
                        {renderStatusChip('In Progress')}
                      </MenuItem>
                      <MenuItem value="Closed">
                        {renderStatusChip('Closed')}
                      </MenuItem>
                    </Select>
                  </FormControl> :

                  <Box>
                    {renderStatusChip(selectedTicket.status)}
                  </Box>
                }
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("VisualTreeView.urgency") : "Urgency"}

                </Typography>
                {isSupport ?
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedTicket.urgency || 'MEDIUM'}
                      onChange={async (e) => {
                        try {
                          const newUrgency = e.target.value;

                          const response = await ticketsApi.update(selectedTicket.id, { urgency: newUrgency });

                          // Handle different response structures
                          let ticketData = response.data;
                          if (response.data.data) {
                            ticketData = response.data.data;
                          }
                          if (ticketData.ticket) {
                            ticketData = ticketData.ticket;
                          }

                          const updatedTicket = transformTicketData(ticketData);

                          setSelectedTicket(updatedTicket);
                          setTickets(tickets.map((t) => t.id === selectedTicket.id ? updatedTicket : t));

                          enqueueSnackbar('Ticket urgency updated successfully', { variant: 'success' });
                        } catch (error) {
                          console.error('Error updating ticket urgency:', error);
                          enqueueSnackbar('Failed to update ticket urgency', { variant: 'error' });
                        }
                      }}
                      displayEmpty
                      renderValue={(value) =>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {renderUrgencyChip(value)}
                        </Box>
                      }>

                      <MenuItem value="LOW">
                        {renderUrgencyChip('LOW')}
                      </MenuItem>
                      <MenuItem value="MEDIUM">
                        {renderUrgencyChip('MEDIUM')}
                      </MenuItem>
                      <MenuItem value="HIGH">
                        {renderUrgencyChip('HIGH')}
                      </MenuItem>
                      <MenuItem value="URGENT">
                        {renderUrgencyChip('URGENT')}
                      </MenuItem>
                    </Select>
                  </FormControl> :

                  <Box>
                    {renderUrgencyChip(selectedTicket.urgency)}
                  </Box>
                }
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("VisualTreeView.support_type") : "Support Type"}

                </Typography>
                <Box>
                  {renderSupportTypeChip(selectedTicket.support_type)}
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("VisualTreeView.next_step") : "Next Step"}

                </Typography>
                {isSupport ?
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={selectedTicket.next_step || ''}
                    onChange={(e) => handleSelectedTicketChange('next_step', e.target.value)}
                    onBlur={async () => {
                      try {
                        const response = await ticketsApi.update(selectedTicket.id, { next_step: selectedTicket.next_step });

                        // Handle different response structures
                        let ticketData = response.data;
                        if (response.data.data) {
                          ticketData = response.data.data;
                        }
                        if (ticketData.ticket) {
                          ticketData = ticketData.ticket;
                        }

                        const updatedTicket = transformTicketData(ticketData);
                        setSelectedTicket(updatedTicket);
                        setTickets(tickets.map((t) => t.id === selectedTicket.id ? updatedTicket : t));
                        enqueueSnackbar('Next step updated successfully', { variant: 'success' });
                      } catch (error) {
                        console.error('Error updating next step:', error);
                        enqueueSnackbar('Failed to update next step', { variant: 'error' });
                      }
                    }}
                    placeholder={t ? t("Tickets.define_next_step_this_ticket") : "Define the next step for this ticket..."} /> :


                  <Typography variant="body2">
                    {selectedTicket.next_step || 'No next step defined'}
                  </Typography>
                }
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <LocationIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />{t ? t("Tickets.location") : "Location"}

                </Typography>
                {isSupport ?
                  <Box>
                    {selectedTicket.locationId || selectedTicket.location?.id ?
                      <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationIcon color="primary" />
                          <Typography>
                            {locations.find((l) => l.id === (selectedTicket.locationId || selectedTicket.location?.id))?.friendlyName || 'Unknown Location'}
                          </Typography>
                        </Box>
                        <Box>
                          <Button size="small" onClick={() => setEditLocationTreeSelectOpen(true)}>{t ? t("CreateForm.change") : "Change"}

                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={async () => {
                              try {
                                const response = await ticketsApi.update(selectedTicket.id, {
                                  location_id: null
                                });

                                let ticketData = response.data;
                                if (response.data.data) {
                                  ticketData = response.data.data;
                                }
                                if (ticketData.ticket) {
                                  ticketData = ticketData.ticket;
                                }

                                const updatedTicket = transformTicketData(ticketData);
                                setSelectedTicket(updatedTicket);
                                setTickets(tickets.map((t) => t.id === selectedTicket.id ? updatedTicket : t));

                                enqueueSnackbar('Location removed successfully', { variant: 'success' });
                              } catch (error) {
                                console.error('Error removing location:', error);
                                enqueueSnackbar('Failed to remove location', { variant: 'error' });
                              }
                            }}>{t ? t("CreateForm.remove") : "Remove"}


                          </Button>
                        </Box>
                      </Paper> :

                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<LocationIcon />}
                        onClick={() => setEditLocationTreeSelectOpen(true)}
                        sx={{ py: 1.5 }}>{t ? t("CreateForm.select_location") : "Select Location"}


                      </Button>
                    }
                    {selectedTicket.location?.friendlyName &&
                      <Box sx={{ mt: 0.5 }}>
                        <Tooltip title={t ? t("Tickets.click_view_location_details") : "Click to view location details"}>
                          <Typography
                            variant="caption"
                            onClick={() => navigate(`/locations?selectedId=${selectedTicket.locationId || selectedTicket.location.id}`)}
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              textDecoration: 'underline',
                              '&:hover': { color: 'primary.dark' },
                              display: 'inline-block'
                            }}>{t ? t("Tickets.view_location_details") : "View location details \u2192"}


                          </Typography>
                        </Tooltip>
                      </Box>
                    }
                  </Box> :

                  <>
                    {selectedTicket.location?.friendlyName ?
                      <Tooltip title={t ? t("Tickets.click_view_location_details") : "Click to view location details"}>
                        <Typography
                          variant="body2"
                          onClick={() => navigate(`/locations?selectedId=${selectedTicket.locationId || selectedTicket.location.id}`)}
                          sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            textDecoration: 'underline',
                            '&:hover': { color: 'primary.dark' },
                            display: 'inline-block'
                          }}>

                          {selectedTicket.location.friendlyName}
                        </Typography>
                      </Tooltip> :

                      <Typography variant="body2">{t ? t("Tickets.not_specified") : "Not specified"}</Typography>
                    }
                  </>
                }
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <DevicesIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />{t ? t("Tickets.related_device") : "Related Device"}

                </Typography>
                {isSupport ?
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedTicket.related_device_id || ''}
                      onChange={async (e) => {
                        if (!selectedTicket || !selectedTicket.id) {
                          enqueueSnackbar('Invalid ticket selected', { variant: 'error' });
                          return;
                        }

                        try {
                          const newDeviceId = e.target.value || null;
                          const device = devices.find((d) => d.id === newDeviceId);

                          const response = await ticketsApi.update(selectedTicket.id, {
                            related_device_id: newDeviceId,
                            related_device_name: device?.name || device?.friendlyName || null
                          });

                          // Handle different response structures
                          let ticketData = response.data;
                          if (response.data.data) {
                            ticketData = response.data.data;
                          }
                          if (ticketData.ticket) {
                            ticketData = ticketData.ticket;
                          }

                          const updatedTicket = transformTicketData(ticketData);

                          setSelectedTicket(updatedTicket);
                          setTickets(tickets.map((t) => t.id === selectedTicket.id ? updatedTicket : t));

                          enqueueSnackbar('Device updated successfully', { variant: 'success' });
                        } catch (error) {
                          console.error('Error updating device:', error);
                          enqueueSnackbar('Failed to update device', { variant: 'error' });
                        }
                      }}
                      displayEmpty
                      disabled={!selectedTicket.locationId && !selectedTicket.location?.id}
                      renderValue={(selected) => {
                        if (!selected) return <em>{t ? t("Tickets.not_specified") : "Not specified"}</em>;
                        const device = devices.find((d) => d.id === selected);
                        return device?.name || device?.friendlyName || `Device #${selected}`;
                      }}>

                      <MenuItem value="">
                        <em>{t ? t("Tickets.not_specified") : "Not specified"}</em>
                      </MenuItem>
                      {!selectedTicket.locationId && !selectedTicket.location?.id &&
                        <MenuItem disabled>
                          <em>{t ? t("Tickets.select_location_first") : "Select a location first"}</em>
                        </MenuItem>
                      }
                      {devices.
                        filter((device) => {
                          // Only show devices from the selected location
                          const ticketLocationId = selectedTicket.locationId || selectedTicket.location?.id;
                          if (!ticketLocationId) return false;

                          // The API uses deviceLocation field to store location ID
                          const deviceLocationId = device.locationId; // This now maps to deviceLocation from API
                          const isFromLocation = deviceLocationId === ticketLocationId;
                          //console.log(`Device ${device.name} (${device.id}): deviceLocationId=${deviceLocationId}, ticketLocationId=${ticketLocationId}, isFromLocation=${isFromLocation}`);
                          return isFromLocation;
                        }).
                        map((device) =>
                          <MenuItem key={device.id} value={device.id}>
                            {device.name || device.friendlyName || `Device #${device.id}`}
                            {device.deviceType && ` (${device.deviceType})`}
                          </MenuItem>
                        )}
                      {/* Show message if no devices found for this location */}
                      {devices.filter((device) => {
                        const ticketLocationId = selectedTicket.locationId || selectedTicket.location?.id;
                        return ticketLocationId && device.locationId === ticketLocationId;
                      }).length === 0 && (selectedTicket.locationId || selectedTicket.location?.id) &&
                        <MenuItem disabled>
                          <em>{t ? t("Tickets.no_devices_found_this_location") : "No devices found at this location"}</em>
                        </MenuItem>
                      }
                    </Select>
                    {selectedTicket.related_device_id &&
                      <Box sx={{ mt: 0.5 }}>
                        <Tooltip title={t ? t("Tickets.click_view_device_details") : "Click to view device details"}>
                          <Typography
                            variant="caption"
                            onClick={() => navigate(`/devices?selectedId=${selectedTicket.related_device_id}`)}
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              textDecoration: 'underline',
                              '&:hover': { color: 'primary.dark' },
                              display: 'inline-block'
                            }}>{t ? t("Tickets.view_device_details") : "View device details \u2192"}


                          </Typography>
                        </Tooltip>
                      </Box>
                    }
                    {!selectedTicket.locationId && !selectedTicket.location?.id &&
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{t ? t("Tickets.select_location_first_choose_devi") : "Select a location first to choose a device"}

                      </Typography>
                    }
                  </FormControl> :

                  <>
                    {selectedTicket.related_device_name || selectedTicket.related_device_id ?
                      <Tooltip title={t ? t("Tickets.click_view_device_details") : "Click to view device details"}>
                        <Typography
                          variant="body2"
                          onClick={() => navigate(`/devices?selectedId=${selectedTicket.related_device_id}`)}
                          sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            textDecoration: 'underline',
                            '&:hover': { color: 'primary.dark' },
                            display: 'inline-block'
                          }}>

                          {selectedTicket.related_device_name || `Device ID: ${selectedTicket.related_device_id}`}
                        </Typography>
                      </Tooltip> :

                      <Typography variant="body2">{t ? t("Tickets.not_specified") : "Not specified"}</Typography>
                    }
                  </>
                }
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <PersonIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />{t ? t("Tickets.created") : "Created By"}

                </Typography>
                <Typography variant="body2">
                  {selectedTicket.createdByUser?.name || `${selectedTicket.createdByUser?.firstName || ''} ${selectedTicket.createdByUser?.lastName || ''}`.trim() || 'Unknown'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <BusinessIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />{t ? t("Tickets.created_company") : "Created By Company"}

                </Typography>
                <Typography variant="body2">
                  {selectedTicket.createdByCompany?.name || 'Not specified'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <PersonIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />{t ? t("Tickets.assigned") : "Assigned To"}

                </Typography>
                {isSupport ?
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedTicket.support_user_id || selectedTicket.supportUser?.id || ''}
                      onChange={async (e) => {
                        try {
                          const newUserId = e.target.value || null;

                          const response = await ticketsApi.update(selectedTicket.id, { support_user_id: newUserId });

                          // Handle different response structures
                          let ticketData = response.data;
                          if (response.data.data) {
                            ticketData = response.data.data;
                          }
                          if (ticketData.ticket) {
                            ticketData = ticketData.ticket;
                          }

                          const updatedTicket = transformTicketData(ticketData);

                          setSelectedTicket(updatedTicket);
                          setTickets(tickets.map((t) => t.id === selectedTicket.id ? updatedTicket : t));

                          enqueueSnackbar('Ticket assignment updated successfully', { variant: 'success' });
                        } catch (error) {
                          console.error('Error updating ticket assignment:', error);
                          enqueueSnackbar('Failed to update ticket assignment', { variant: 'error' });
                        }
                      }}
                      displayEmpty>

                      <MenuItem value="">
                        <em>{t ? t("Tickets.unassigned") : "Unassigned"}</em>
                      </MenuItem>
                      {supportUsers.map((user) =>
                        <MenuItem key={user.id} value={user.id}>
                          {user.name || user.username || `${user.firstName || ''} ${user.lastName || ''}`.trim() || `User #${user.id}`}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl> :

                  <Typography variant="body2">
                    {selectedTicket.supportUser ?
                      selectedTicket.supportUser.name || `${selectedTicket.supportUser.firstName || ''} ${selectedTicket.supportUser.lastName || ''}`.trim() :
                      'Not assigned'
                    }
                  </Typography>
                }
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <TimeIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />{t ? t("LocationDetails.created") : "Created"}

                </Typography>
                <Typography variant="body2">
                  {formatDate(selectedTicket.created_at)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getTimeDifference(selectedTicket.created_at)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <TimeIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />{t ? t("LocationDetails.last_updated") : "Last Updated"}

                </Typography>
                <Typography variant="body2">
                  {formatDate(selectedTicket.updated_at)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getTimeDifference(selectedTicket.updated_at)}
                </Typography>
              </Box>
            </Paper>

            {/* Quick Actions Card */}
            <Paper className="card-animated card-enter" sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" gutterBottom>{t ? t("LocationDetails.quick_actions") : "Quick Actions"}

              </Typography>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<MessageIcon />}
                sx={{ mb: 2 }}
                onClick={() => {
                  setTabValue(0);
                  setTimeout(() => {
                    document.querySelector('textarea, input[type="text"]')?.focus();
                  }, 100);
                }}>{t ? t("Tickets.add_message") : "Add Message"}


              </Button>

              {isSupport &&
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  startIcon={<AssignmentIcon />}
                  sx={{ mb: 2 }}
                  onClick={() => {
                    // Navigate to create field intervention with ticket pre-filled
                    navigate(`/field-interventions/new?ticket_id=${selectedTicket.id}&location_id=${selectedTicket.locationId || ''}&issue_description=${encodeURIComponent(selectedTicket.description || '')}`);
                  }}>{t ? t("Tickets.create_field_intervention") : "Create Field Intervention"}


                </Button>
              }

              {selectedTicket.status !== 'Closed' &&
                <Button
                  fullWidth
                  variant="outlined"
                  color="success"
                  startIcon={<CheckIcon />}
                  sx={{ mb: 2 }}
                  onClick={async () => {
                    try {
                      const response = await ticketsApi.update(selectedTicket.id, {
                        status: 'Closed',
                        next_step: 'Resolved'
                      });

                      // Handle different response structures
                      let ticketData = response.data;
                      if (response.data.data) {
                        ticketData = response.data.data;
                      }
                      if (ticketData.ticket) {
                        ticketData = ticketData.ticket;
                      }

                      const updatedTicket = transformTicketData(ticketData);

                      setSelectedTicket(updatedTicket);
                      setTickets(tickets.map((t) =>
                        t.id === selectedTicket.id ? updatedTicket : t
                      ));

                      enqueueSnackbar('Ticket marked as resolved', { variant: 'success' });
                    } catch (error) {
                      console.error('Error marking ticket as resolved:', error);
                      enqueueSnackbar('Failed to mark ticket as resolved', { variant: 'error' });
                    }
                  }}>{t ? t("Tickets.mark_as_resolved") : "Mark as Resolved"}


                </Button>
              }

              {selectedTicket.status === 'Closed' &&
                <Button
                  fullWidth
                  variant="outlined"
                  color="warning"
                  startIcon={<RefreshIcon />}
                  sx={{ mb: 2 }}
                  onClick={async () => {
                    try {
                      const response = await ticketsApi.update(selectedTicket.id, {
                        status: 'In Progress',
                        next_step: 'Reopened'
                      });

                      // Handle different response structures
                      let ticketData = response.data;
                      if (response.data.data) {
                        ticketData = response.data.data;
                      }
                      if (ticketData.ticket) {
                        ticketData = ticketData.ticket;
                      }

                      const updatedTicket = transformTicketData(ticketData);

                      setSelectedTicket(updatedTicket);
                      setTickets(tickets.map((t) =>
                        t.id === selectedTicket.id ? updatedTicket : t
                      ));

                      enqueueSnackbar('Ticket reopened successfully', { variant: 'success' });
                    } catch (error) {
                      console.error('Error reopening ticket:', error);
                      enqueueSnackbar('Failed to reopen ticket', { variant: 'error' });
                    }
                  }}>{t ? t("Tickets.reopen_ticket") : "Reopen Ticket"}


                </Button>
              }

              <Button
                fullWidth
                variant="outlined"
                color="warning"
                startIcon={<FlagIcon />}
                onClick={async () => {
                  try {
                    // This would typically open a dialog to change priority
                    const newUrgency = selectedTicket.urgency === 'URGENT' ?
                      'MEDIUM' :
                      'URGENT';

                    const response = await ticketsApi.update(selectedTicket.id, {
                      urgency: newUrgency
                    });

                    // Handle different response structures
                    let ticketData = response.data;
                    if (response.data.data) {
                      ticketData = response.data.data;
                    }
                    if (ticketData.ticket) {
                      ticketData = ticketData.ticket;
                    }

                    const updatedTicket = transformTicketData(ticketData);

                    setSelectedTicket(updatedTicket);
                    setTickets(tickets.map((t) =>
                      t.id === selectedTicket.id ? updatedTicket : t
                    ));

                    const message = newUrgency === 'URGENT' ?
                      'Ticket urgency escalated to URGENT' :
                      `Ticket urgency changed to ${newUrgency}`;
                    enqueueSnackbar(message, { variant: 'success' });
                  } catch (error) {
                    console.error('Error updating urgency:', error);
                    enqueueSnackbar('Failed to update ticket urgency', { variant: 'error' });
                  }
                }}>

                {selectedTicket.urgency === 'URGENT' ?
                  'Lower Urgency' :
                  'Escalate Urgency'
                }
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}>

          <DialogTitle>{t ? t("LocationDetails.confirm_deletion") : "Confirm Deletion"}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t ? t("Tickets.are_you_sure_you_want_delete_ticket") : "Are you sure you want to delete ticket #"}
              {selectedTicket.id}{t ? t("LocationDetails.this_action_cannot_be_undone") : "? This action cannot be undone."}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>{t ? t("LocationDialog.cancel") : "Cancel"}

            </Button>
            <Button color="error" onClick={confirmDeleteTicket}>{t ? t("DeviceTable.delete") : "Delete"}

            </Button>
          </DialogActions>
        </Dialog>

        {/* Save as Known Issue Dialog - Only for Support Staff */}
        {isSupport &&
          <SaveAsKnownIssue
            ticket={selectedTicket}
            open={saveAsKnownIssueOpen}
            onClose={() => setSaveAsKnownIssueOpen(false)}
            onSaved={(issueId) => {
              setSaveAsKnownIssueOpen(false);
              // Optionally refresh ticket or show success message
              //console.log('Saved as known issue with ID:', issueId);
            }} />

        }

        {/* Select Existing Solution Dialog - Only for Support Staff */}
        {isSupport &&
          <SelectExistingSolution
            ticketId={selectedTicket?.id}
            open={selectSolutionOpen}
            onClose={() => setSelectSolutionOpen(false)}
            onApplied={() => {
              setSelectSolutionOpen(false);
              // Optionally refresh ticket to show applied solutions
              fetchTickets();
            }} />

        }

        {/* TreeSelect for Location Selection in View Mode */}
        <TreeSelect
          open={editLocationTreeSelectOpen}
          onClose={() => setEditLocationTreeSelectOpen(false)}
          onSelect={async (location) => {
            if (!selectedTicket || !selectedTicket.id) {
              enqueueSnackbar('Invalid ticket selected', { variant: 'error' });
              return;
            }

            try {
              const newLocationId = location?.id || null;
              const oldLocationId = selectedTicket.locationId || selectedTicket.location?.id;

              const response = await ticketsApi.update(selectedTicket.id, {
                location_id: newLocationId
              });

              //console.log('Location update response:', response);

              // Handle different response structures
              let ticketData = response.data;
              if (response.data.data) {
                ticketData = response.data.data;
              }
              if (ticketData.ticket) {
                ticketData = ticketData.ticket;
              }

              const updatedTicket = transformTicketData(ticketData);
              //console.log('Updated ticket after transform:', updatedTicket);

              setSelectedTicket(updatedTicket);
              setTickets(tickets.map((t) => t.id === selectedTicket.id ? updatedTicket : t));

              // Fetch devices from new location if location changed
              if (newLocationId && newLocationId !== oldLocationId) {
                //console.log('Location changed, fetching devices for new location:', newLocationId);
                await fetchDevices(newLocationId, false);

                // Clear any selected device if it's not from the new location
                if (selectedTicket.related_device_id) {
                  // Check if current device belongs to new location
                  const currentDeviceInNewLocation = devices.find((d) =>
                    d.id === selectedTicket.related_device_id &&
                    d.locationId === newLocationId
                  );

                  if (!currentDeviceInNewLocation) {
                    //console.log('Current device not in new location, clearing device selection');
                    // Update ticket to remove device assignment
                    await ticketsApi.update(selectedTicket.id, {
                      related_device_id: null,
                      related_device_name: null
                    });

                    const clearedTicket = {
                      ...updatedTicket,
                      related_device_id: null,
                      related_device_name: null
                    };
                    setSelectedTicket(clearedTicket);
                    setTickets(tickets.map((t) => t.id === selectedTicket.id ? clearedTicket : t));
                  }
                }
              } else if (!newLocationId) {
                // Location removed, clear devices
                setDevices([]);
                if (selectedTicket.related_device_id) {
                  //console.log('Location removed, clearing device selection');
                  await ticketsApi.update(selectedTicket.id, {
                    related_device_id: null,
                    related_device_name: null
                  });

                  const clearedTicket = {
                    ...updatedTicket,
                    related_device_id: null,
                    related_device_name: null
                  };
                  setSelectedTicket(clearedTicket);
                  setTickets(tickets.map((t) => t.id === selectedTicket.id ? clearedTicket : t));
                }
              }

              setEditLocationTreeSelectOpen(false);
              enqueueSnackbar('Location updated successfully', { variant: 'success' });
            } catch (error) {
              console.error('Error updating location:', error);
              enqueueSnackbar('Failed to update location', { variant: 'error' });
            }
          }}
          data={locationsForTreeSelect}
          selectedId={selectedTicket?.locationId || selectedTicket?.location?.id}
          title={t ? t("Tickets.select_location_ticket") : "Select Location for Ticket"}
          type="location"
          allowNone={true} />


        {/* Physical Intervention Dialog */}
        <Dialog
          open={interventionDialogOpen}
          onClose={() => {
            //console.log('Closing intervention dialog via onClose');
            if (!sendingIntervention) {
              setInterventionDialogOpen(false);
              // Reset form when closing
              setInterventionDate('');
              setInterventionTime('09:00');
              setInterventionNotes('');
              setInterventionDeviceId('');
              setInterventionDevices([]);
            }
          }}
          maxWidth="sm"
          fullWidth>

          <DialogTitle>{t ? t("Tickets.request_physical_intervention") : "Request Physical Intervention"}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>{t ? t("Tickets.request_physical_intervention_this") : "Request a physical intervention for this ticket. An email notification will be sent to all followers of this ticket."}

            </DialogContentText>

            <TextField
              label={t ? t("Tickets.intervention_date_optional") : "Intervention Date (Optional)"}
              type="date"
              fullWidth
              value={interventionDate}
              onChange={(e) => setInterventionDate(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
              InputLabelProps={{
                shrink: true
              }}
              helperText={t ? t("Tickets.select_scheduled_date_interv") : "Select the scheduled date for the intervention"} />

            <TextField
              label={t ? t("Tickets.intervention_time_optional") : "Intervention Time (Optional)"}
              type="time"
              fullWidth
              value={interventionTime}
              onChange={(e) => setInterventionTime(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{
                shrink: true
              }}
              helperText={t ? t("Tickets.select_scheduled_time_interv") : "Select the scheduled time for the intervention (defaults to 9:00 AM)"} />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="intervention-device-label">{t ? t("Tickets.device_optional") : "Device (Optional)"}</InputLabel>
              <Select
                labelId="intervention-device-label"
                id="intervention-device-select"
                value={interventionDeviceId}
                label={t ? t("Tickets.device_optional") : "Device (Optional)"}
                onChange={(e) => setInterventionDeviceId(e.target.value)}
                disabled={loadingInterventionDevices || interventionDevices.length === 0}>

                <MenuItem value="">
                  <em>{t ? t("Tickets.no_specific_device") : "No specific device"}</em>
                </MenuItem>
                {interventionDevices.map((device) =>
                  <MenuItem key={device.id} value={device.id}>
                    {device.friendlyName || device.name || `Device #${device.id}`}
                    {device.deviceType && ` (${device.deviceType})`}
                  </MenuItem>
                )}
                {/* Show message if no devices found for this location */}
                {!loadingInterventionDevices && interventionDevices.length === 0 && (selectedTicket.locationId || selectedTicket.location?.id) &&
                  <MenuItem disabled>
                    <em>{t ? t("Tickets.no_devices_found_this_location") : "No devices found at this location"}</em>
                  </MenuItem>
                }
              </Select>
              {loadingInterventionDevices &&
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>{t ? t("Tickets.loading_devices") : "Loading devices..."}

                </Typography>
              }
              {!loadingInterventionDevices && interventionDevices.length === 0 &&
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>{t ? t("Tickets.no_devices_found_this_location") : "No devices found at this location"}

                </Typography>
              }
            </FormControl>

            <TextField
              label={t ? t("Tickets.notes_optional") : "Notes (Optional)"}
              multiline
              rows={4}
              fullWidth
              value={interventionNotes}
              onChange={(e) => setInterventionNotes(e.target.value)}
              placeholder={t ? t("Tickets.add_any_additional_notes_about_inter") : "Add any additional notes about the intervention..."} />

          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                //console.log('Cancel button clicked');
                setInterventionDialogOpen(false);
                setInterventionDate('');
                setInterventionTime('09:00');
                setInterventionNotes('');
                setInterventionDeviceId('');
                setInterventionDevices([]);
              }}
              disabled={sendingIntervention}>{t ? t("LocationDialog.cancel") : "Cancel"}


            </Button>
            <Button
              onClick={handleRequestIntervention}
              variant="contained"
              color="warning"
              disabled={sendingIntervention}
              startIcon={sendingIntervention ? <CircularProgress size={20} /> : <BuildIcon />}>

              {sendingIntervention ? 'Sending...' : 'Send Request'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reschedule Intervention Dialog */}
        <Dialog
          open={rescheduleDialogOpen}
          onClose={() => {
            if (!reschedulingIntervention) {
              setRescheduleDialogOpen(false);
              setRescheduleDate('');
              setRescheduleTime('09:00');
              setRescheduleNotes('');
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t ? t("Tickets.reschedule_intervention") : "Reschedule Intervention"}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t ? t("Tickets.reschedule_calendar_note") : "Change the scheduled date and time for this intervention. Calendar items and notifications will be updated automatically."}
            </DialogContentText>

            <TextField
              label={t ? t("Tickets.new_intervention_date") : "New Intervention Date"}
              type="date"
              fullWidth
              required
              value={rescheduleDate}
              onChange={(e) => setRescheduleDate(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
              InputLabelProps={{ shrink: true }}
              helperText={t ? t("Tickets.select_new_date_intervention") : "Select the new date for the intervention"}
            />

            <TextField
              label={t ? t("Tickets.new_intervention_time") : "New Intervention Time"}
              type="time"
              fullWidth
              value={rescheduleTime}
              onChange={(e) => setRescheduleTime(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              helperText={t ? t("Tickets.select_new_time_intervention") : "Select the new time for the intervention"}
            />

            <TextField
              label={t ? t("Tickets.reschedule_reason_optional") : "Reschedule Reason (Optional)"}
              multiline
              rows={3}
              fullWidth
              value={rescheduleNotes}
              onChange={(e) => setRescheduleNotes(e.target.value)}
              placeholder={t ? t("Tickets.reschedule_reason_placeholder") : "Provide a reason for rescheduling (will be included in the notification email)..."}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setRescheduleDialogOpen(false);
                setRescheduleDate('');
                setRescheduleTime('09:00');
                setRescheduleNotes('');
              }}
              disabled={reschedulingIntervention}
            >
              {t ? t("Common.cancel") : "Cancel"}
            </Button>
            <Button
              onClick={handleRescheduleIntervention}
              variant="contained"
              color="primary"
              disabled={reschedulingIntervention || !rescheduleDate}
              startIcon={reschedulingIntervention ? <CircularProgress size={20} /> : <EventIcon />}
            >
              {reschedulingIntervention ? (t ? t("Tickets.rescheduling") : "Rescheduling...") : (t ? t("Tickets.reschedule") : "Reschedule")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Intervention Dialog */}
        <Dialog
          open={cancelDialogOpen}
          onClose={() => {
            if (!cancellingIntervention) {
              setCancelDialogOpen(false);
              setCancelReason('');
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t ? t("Tickets.cancel_intervention") : "Cancel Intervention"}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t ? t("Tickets.cancel_confirmation") : "Are you sure you want to cancel this intervention? Calendar items will be removed and all followers will be notified."}
            </DialogContentText>

            <TextField
              label={t ? t("Tickets.cancellation_reason") : "Cancellation Reason"}
              multiline
              rows={4}
              fullWidth
              required
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder={t ? t("Tickets.cancellation_reason_placeholder") : "Please provide a reason for cancelling the intervention (required)..."}
              helperText={t ? t("Tickets.cancellation_reason_note") : "This reason will be included in the notification email"}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setCancelDialogOpen(false);
                setCancelReason('');
              }}
              disabled={cancellingIntervention}
            >
              {t ? t("Tickets.go_back") : "Go Back"}
            </Button>
            <Button
              onClick={handleCancelIntervention}
              variant="contained"
              color="error"
              disabled={cancellingIntervention || !cancelReason || cancelReason.trim() === ''}
              startIcon={cancellingIntervention ? <CircularProgress size={20} /> : <CancelIcon />}
            >
              {cancellingIntervention ? (t ? t("Tickets.cancelling") : "Cancelling...") : (t ? t("Tickets.cancel_intervention") : "Cancel Intervention")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Intervention Selection Dialog */}
        <Dialog
          open={interventionSelectionDialogOpen}
          onClose={() => setInterventionSelectionDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{t ? t("Tickets.select_intervention") : "Select Intervention Report"}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t ? t("Tickets.multiple_interventions_message") : "This ticket has multiple interventions. Please select which one you would like to view:"}
            </DialogContentText>
            <List>
              {linkedInterventions.map((intervention) => (
                <ListItem
                  key={intervention.id}
                  button
                  onClick={() => {
                    navigate(`/field-interventions/${intervention.id}`);
                    setInterventionSelectionDialogOpen(false);
                  }}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon>
                    <AssignmentIcon color={
                      intervention.status === 'completed' ? 'success' :
                        intervention.status === 'cancelled' ? 'error' :
                          intervention.status === 'in_progress' ? 'warning' :
                            'default'
                    } />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">
                          {`Intervention #${intervention.id}`}
                        </Typography>
                        <Chip
                          label={intervention.status || 'pending'}
                          size="small"
                          color={
                            intervention.status === 'completed' ? 'success' :
                              intervention.status === 'cancelled' ? 'error' :
                                intervention.status === 'in_progress' ? 'warning' :
                                  'default'
                          }
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        {intervention.intervention_date && (
                          <Typography variant="body2" component="span">
                            {`Date: ${new Date(intervention.intervention_date).toLocaleDateString()}`}
                          </Typography>
                        )}
                        {intervention.outcome && (
                          <Typography variant="body2" component="span" sx={{ ml: 2 }}>
                            {`Outcome: ${intervention.outcome}`}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInterventionSelectionDialogOpen(false)}>
              {t ? t("Common.cancel") : "Cancel"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>);

  }

  // Edit Ticket
  if (viewMode === 'edit' && selectedTicket) {
    return (
      <Box
        className="page-transition-enter"
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box'
        }}>

        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => setViewMode('view')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" className="slide-down">{t ? t("VisualTreeView.edit_ticket") : "Edit Ticket #"}{selectedTicket.id}</Typography>
        </Box>

        <Paper className="card-animated card-enter" sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t ? t("VisualTreeView.title") : "Title"}
                value={selectedTicket.title || ''}
                onChange={(e) => handleSelectedTicketChange('title', e.target.value)} />

            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t ? t("LocationDialog.description") : "Description"}
                value={selectedTicket.description}
                onChange={(e) => handleSelectedTicketChange('description', e.target.value)}
                multiline
                rows={4}
                required
                error={!selectedTicket.description} />

            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t ? t("VisualTreeView.steps_already_tried") : "Steps Already Tried"}
                value={selectedTicket.steps_tried || ''}
                onChange={(e) => handleSelectedTicketChange('steps_tried', e.target.value)}
                multiline
                rows={3} />

            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t ? t("DeviceTable.status") : "Status"}</InputLabel>
                <Select
                  value={selectedTicket.status}
                  label={t ? t("DeviceTable.status") : "Status"}
                  onChange={(e) => handleSelectedTicketChange('status', e.target.value)}>

                  <MenuItem value="Open">{t ? t("VisualTreeView.open") : "Open"}</MenuItem>
                  <MenuItem value="In Progress">{t ? t("VisualTreeView.progress") : "In Progress"}</MenuItem>
                  <MenuItem value="Closed">{t ? t("VisualTreeView.closed") : "Closed"}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t ? t("VisualTreeView.urgency") : "Urgency"}</InputLabel>
                <Select
                  value={selectedTicket.urgency}
                  label={t ? t("VisualTreeView.urgency") : "Urgency"}
                  onChange={(e) => handleSelectedTicketChange('urgency', e.target.value)}>

                  <MenuItem value="LOW">{t ? t("VisualTreeView.low") : "Low"}</MenuItem>
                  <MenuItem value="MEDIUM">{t ? t("VisualTreeView.medium") : "Medium"}</MenuItem>
                  <MenuItem value="HIGH">{t ? t("VisualTreeView.high") : "High"}</MenuItem>
                  <MenuItem value="URGENT">{t ? t("VisualTreeView.urgent") : "Urgent"}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t ? t("VisualTreeView.support_type") : "Support Type"}</InputLabel>
                <Select
                  value={selectedTicket.support_type}
                  label={t ? t("VisualTreeView.support_type") : "Support Type"}
                  onChange={(e) => handleSelectedTicketChange('support_type', e.target.value)}>

                  <MenuItem value="domotics">{t ? t("VisualTreeView.domotics") : "Domotics"}</MenuItem>
                  <MenuItem value="access_control">{t ? t("VisualTreeView.access_control") : "Access Control"}</MenuItem>
                  <MenuItem value="tablet">{t ? t("VisualTreeView.tablet") : "Tablet"}</MenuItem>
                  <MenuItem value="emergency_system">{t ? t("VisualTreeView.emergency_system") : "Emergency System"}</MenuItem>
                  <MenuItem value="it_pc">{t ? t("VisualTreeView.itpc") : "IT/PC"}</MenuItem>
                  <MenuItem value="other">{t ? t("VisualTreeView.other") : "Other"}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Support Staff Only: Assigned To */}
            {isSupport &&
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t ? t("Tickets.assign_support_user") : "Assign To Support User"}</InputLabel>
                  <Select
                    value={selectedTicket.support_user_id || selectedTicket.supportUser?.id || ''}
                    label={t ? t("Tickets.assign_support_user") : "Assign To Support User"}
                    onChange={(e) => {
                      handleSelectedTicketChange('support_user_id', e.target.value || null);
                      // Also update the supportUser object for display
                      const user = supportUsers.find((u) => u.id === e.target.value);
                      if (user) {
                        handleSelectedTicketChange('supportUser', user);
                      }
                    }}>

                    <MenuItem value="">
                      <em>{t ? t("Tickets.unassigned") : "Unassigned"}</em>
                    </MenuItem>
                    {supportUsers.map((user) =>
                      <MenuItem key={user.id} value={user.id}>
                        {user.name || user.username || `${user.firstName || ''} ${user.lastName || ''}`.trim() || `User #${user.id}`}
                        {user.email && ` (${user.email})`}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
            }

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>{t ? t("Tickets.location") : "Location"}

                </Typography>
                {selectedTicket.locationId ?
                  <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationIcon color="primary" />
                      <Typography>
                        {locations.find((l) => l.id === selectedTicket.locationId)?.friendlyName || 'Unknown Location'}
                      </Typography>
                    </Box>
                    <Box>
                      <Button size="small" onClick={() => setLocationTreeSelectOpen(true)}>{t ? t("CreateForm.change") : "Change"}

                      </Button>
                      <Button size="small" color="error" onClick={() => handleSelectedTicketChange('locationId', null)}>{t ? t("CreateForm.remove") : "Remove"}

                      </Button>
                    </Box>
                  </Paper> :

                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<LocationIcon />}
                    onClick={() => setLocationTreeSelectOpen(true)}
                    sx={{ py: 1.5 }}>{t ? t("CreateForm.select_location") : "Select Location"}


                  </Button>
                }
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>{t ? t("Tickets.related_device_optional") : "Related Device (Optional)"}

                </Typography>
                {selectedTicket.related_device_id || selectedTicket.related_device_name ?
                  <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <DevicesIcon color="primary" />
                      <Typography>
                        {selectedTicket.related_device_name || `Device ID: ${selectedTicket.related_device_id}`}
                      </Typography>
                    </Box>
                    <Box>
                      <Button size="small" onClick={async () => {
                        const currentLocationId = selectedTicket.locationId;
                        if (currentLocationId) {
                          await fetchDevices(currentLocationId, false);
                        }
                        setDeviceDialogOpen(true);
                      }}>{t ? t("CreateForm.change") : "Change"}

                      </Button>
                      <Button size="small" color="error" onClick={() => {
                        handleSelectedTicketChange('related_device_id', null);
                        handleSelectedTicketChange('related_device_name', null);
                      }}>{t ? t("CreateForm.remove") : "Remove"}

                      </Button>
                    </Box>
                  </Paper> :

                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<DevicesIcon />}
                    onClick={async () => {
                      const currentLocationId = selectedTicket.locationId;
                      if (currentLocationId) {
                        await fetchDevices(currentLocationId, false);
                      }
                      setDeviceDialogOpen(true);
                    }}
                    sx={{ py: 1.5 }}>{t ? t("Tickets.select_device") : "Select Device"}


                  </Button>
                }
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t ? t("VisualTreeView.next_step") : "Next Step"}
                value={selectedTicket.next_step || ''}
                onChange={(e) => handleSelectedTicketChange('next_step', e.target.value)} />

            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  onClick={() => setViewMode('view')}>{t ? t("LocationDialog.cancel") : "Cancel"}


                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveTicket}
                  disabled={!selectedTicket.description}>{t ? t("LocationManagement.save_changes") : "Save Changes"}


                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* TreeSelect for Location Selection in Edit Mode */}
        <TreeSelect
          open={locationTreeSelectOpen}
          onClose={() => setLocationTreeSelectOpen(false)}
          onSelect={async (location) => {
            const newLocationId = location?.id || null;
            const oldLocationId = selectedTicket.locationId;

            // Update the ticket state
            handleSelectedTicketChange('locationId', newLocationId);

            // Fetch devices for the new location
            if (newLocationId && newLocationId !== oldLocationId) {
              //console.log('Location changed in edit mode, fetching devices for:', newLocationId);
              await fetchDevices(newLocationId, false);

              // Clear device selection if current device is not from new location
              if (selectedTicket.related_device_id) {
                //console.log('Clearing device selection due to location change');
                handleSelectedTicketChange('related_device_id', null);
                handleSelectedTicketChange('related_device_name', null);
              }
            } else if (!newLocationId) {
              // Location removed, clear devices and device selection
              setDevices([]);
              handleSelectedTicketChange('related_device_id', null);
              handleSelectedTicketChange('related_device_name', null);
            }

            setLocationTreeSelectOpen(false);
          }}
          data={locationsForTreeSelect}
          selectedId={selectedTicket.locationId}
          title={t ? t("Tickets.select_location_ticket") : "Select Location for Ticket"}
          type="location"
          allowNone={true} />


        {/* Device Selection Dialog */}
        <Dialog
          open={deviceDialogOpen}
          onClose={() => setDeviceDialogOpen(false)}
          maxWidth="sm"
          fullWidth>

          <DialogTitle>{t ? t("Tickets.select_device") : "Select Device"}</DialogTitle>
          <DialogContent>
            {loadingDevices ?
              <Box display="flex" justifyContent="center" py={3}>
                <div className="spinner" />
              </Box> :
              devices.length > 0 ?
                <List>
                  {devices.
                    filter((device) => {
                      const ticketLocationId = selectedTicket.locationId;
                      if (!ticketLocationId) return true; // Show all devices if no location selected
                      // Use the correct field mapping for device location
                      return device.locationId === ticketLocationId;
                    }).
                    map((device) =>
                      <ListItem
                        key={device.id}
                        button
                        onClick={() => {
                          handleSelectedTicketChange('related_device_id', device.id);
                          handleSelectedTicketChange('related_device_name', device.name);
                          setDeviceDialogOpen(false);
                        }}
                        selected={selectedTicket.related_device_id === device.id}>

                        <ListItemIcon>
                          <DevicesIcon color={selectedTicket.related_device_id === device.id ? 'primary' : 'inherit'} />
                        </ListItemIcon>
                        <ListItemText
                          primary={device.name}
                          secondary={device.deviceType ? `Type: ${device.deviceType}` : null} />

                      </ListItem>
                    )}
                  {/* Show message if no devices found for this location */}
                  {selectedTicket.locationId && devices.filter((device) => device.locationId === selectedTicket.locationId).length === 0 &&
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">{t ? t("Tickets.no_devices_found_this_location") : "No devices found at this location"}

                      </Typography>
                    </Box>
                  }
                </List> :

                <Alert severity="info" className="slide-up">
                  {selectedTicket.locationId ?
                    'No devices found at this location. The location may not have any registered devices.' :
                    'Please select a location first to see available devices.'
                  }
                </Alert>
            }
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeviceDialogOpen(false)}>{t ? t("LocationDialog.cancel") : "Cancel"}</Button>
          </DialogActions>
        </Dialog>
      </Box>);

  }

  // Fallback
  return (
    <Box
      className="page-transition-enter"
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>

      <Typography variant="h4" className="slide-down">{t ? t("Tickets.something_went_wrong") : "Something went wrong"}</Typography>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => {
          setViewMode('list');
          setSelectedTicket(null);
        }}
        sx={{ mt: 2 }}>{t ? t("Tickets.return_ticket_list") : "Return to Ticket List"}


      </Button>
    </Box>);

};

// TabPanel component for tabs
const TabPanel = (props) => {
  const { t } = useThemeSettings();
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}>

      {value === index &&
        <Box>
          {children}
        </Box>
      }
    </div>);

};

export default TicketManagementSystem;